import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { themeVar as colors } from "../design-tokens";
import { AppShell, type ShellStep } from "../ui/AppShell";
import { measure } from "../ui/measures";
import { IntakeScreen, EMPTY_INTAKE, type IntakeState } from "./IntakeScreen";
import { ExtractProgress, type ExtractPhase } from "./ExtractProgress";
import { AddressMismatchPrompt, addressesAgree, normalizeAddress, type AddressMismatch } from "./AddressMismatchPrompt";
import { ConfirmGrid } from "./ConfirmGrid";
import { displayOrder, primacyRecord } from "./compLadder";
import { CompetingListings, PriorSaleNotice, ReadingNotes } from "./CompetingListings";
import { subjectOverrideFor, type SubjectOverrideEntry } from "./lotConflict";
import { ReadinessGate, CompsStepGate, SubjectStepGate, hasEnoughComps, isGridReady, isUsableComp, missingRequiredFields } from "./ReadinessGate";
import { SubjectCarryOver } from "./SubjectCarryOver";
import { tickTopRecommended, confirmTicked, confirmSelected } from "./bestMatchTicks";
import { buildStartFailure } from "./buildStartError";
import { UpgradesPanel } from "./UpgradesPanel";
import { HomeownerName } from "./HomeownerName";
import { SubjectPhotoUpload } from "./SubjectPhotoUpload";
import { SubjectPhotoPicker, useSubjectPhotoOptions } from "./SubjectPhotoPicker";
import { photoKey, photoOptionToDataUri, type SubjectPhotoOption } from "./subjectPhotos";
import { ReviewSummary } from "./ReviewSummary";
import { LocationMap } from "./LocationMap";
import { compMapQuery, serverRefusedLocation, storedLocation } from "./compMapModel";
import { CompSearch, glaSourceWords } from "./CompSearch";
import { numFromGrid } from "./compCriteria";
import { ActivesSearch, type ActivesView } from "./ActivesSearchPanel";
import { buildPins } from "./addressPin";
import type { MarketConditions } from "./MarketConditionsPanel";
import { carryOver, countySubjectToGrid, gridFromSubject, isFieldConfirmed, mergeGrid,
         sourcedAdditions } from "./subjectGrid";
import { saveDraft, loadDraft, loadServerDraft, clearAllDrafts, sweepExpiredDrafts, markHeld }
  from "./draftStore";
import { PUBLIC_RECORDS_UNAVAILABLE, cadRecordTag, livingAreaFlagNote } from "./cadPick";
import type { CadPick, SubjectChangeEntry } from "./cadPick";
import { SUBJECT_FIELD_SCHEMA } from "./fieldSchema";
import { FileReadReceipt } from "./FileReadReceipt";
import { describeSubjectChange, InvalidationPrompt } from "./EditImpact";
import { NarrativeReportScreen, resolveView, type ReportView } from "./NarrativeReportScreen";
import { PersonaDownloads } from "./PersonaDownloads";
import {
  startExtract,
  getJob,
  submitConfirmed,
  lookupCountySubject,
  getLoadedCounties,
  cotalityLookupSubject,
  cadAvailableForAddress,
  getCascadeStatus,
  resolveCascade,
  rankComps,
  type CadAvailability,
} from "./api";
import { CompMlsRow } from "../mlsAccess/CompMlsRow";
import { withMlsPhotos } from "../mlsAccess/api";
import { CadRequiredPanel, CascadeNotices, CouldNotAskPanel, FoundLine, GapQuestions, NoCompsCard, SourceDiagnosticsPanel, UploadSkippedPanel } from "./CascadePanels";
import { useMlsOffered } from "../mlsAccess/useMlsOffered";
import { isLimitReached } from "../lib/usageApi";
import { refreshUsage } from "../lib/useUsage";
import { activesSearchClaim, activesSearchLine } from "./activesSearch";
import {
  agentSubjectFromGrid, cascadeMeta, compsFromCascade, couldNotAsk, heldRowsFromCascade, foundLine, gridFromCascadeSubject,
  publicRecordsDormant,
  mergeCascadeSubject, noticesFromGates, cascadeArgsWithPick, subjectWithoutProviderFields, offlinePayload, type CascadeMeta, type CascadeResolveArgs,
  type CascadeResolveResult, type CascadeStatus, type CotalityOfflineBody,
} from "./cascade";
import { DEFAULT_PERSONA, EXTRACT_STAGES, isClosedSale, isCompetingListing, normalizeCondition,
         type PersonaId, type ReportTier, tierFromParam } from "./constants";
import type { AmbJobPublic, CandidateComp, LoadedCounty, SubjectGrid, ValueAdjustments, FileReadStatus } from "./types";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";
import { blockStyle } from "../ui/Block";

const EXTRACT_TERMINAL: AmbJobPublic["status"][] = ["review_ready", "failed", "needs_input"];
const BUILD_TERMINAL: AmbJobPublic["status"][] = ["done", "failed", "needs_input"];

/** UI-101 stall guard: polls are 2s apart, so this is the number of consecutive polls reporting the
 * exact same status AND stage before the page says anything.
 *
 * RAISED FROM 45 (90 s) TO 150 (5 MINUTES) ON 2026-08-04, because 90 seconds was measurably wrong.
 * A PDF whose fonts carry no character map has no usable text layer at all and must be read page by
 * page by the local OCR floor, at roughly 13 seconds a page — a real 20-page NTREIS export is
 * therefore about four minutes of genuine work. The page was calling that wedged at the 90-second
 * mark and telling the owner to start over, on a job that was running perfectly and would have
 * finished. Advising someone to abandon four minutes of correct work at the 90-second mark is worse
 * than saying nothing.
 *
 * The signature includes the OCR page counter, so a healthy pass normally re-arms this on every
 * page and never reaches the threshold at all. The threshold is what catches the case where even
 * the page counter stops moving — and for that, five minutes of complete silence is the honest
 * bar. See `EXPECTED_WORST_CASE` below for what the user is told while they wait. */
const STALL_TICKS = 150;

/** What a slow read actually costs, stated up front. The owner's measured worst case is a 20-page
 * scanned/unmapped PDF on the local OCR path; hosted OCR (tier 2) turns the same file into seconds
 * when it is configured. Quoted as a ceiling, not an estimate, because a ceiling that holds is
 * reassuring and an estimate that slips is not. */
const EXPECTED_WORST_CASE = "up to 10 minutes";

/** Where an in-progress review is parked so a refresh doesn't destroy 34 seconds of extraction
 * (UI-009). Session-scoped, cleared by the browser when the tab closes. This holds property facts
 * the user is already looking at on screen — never a credential or session token, which the
 * project's auth module rules out categorically.
 *
 * SINCE 2026-08-05 IT ALSO HOLDS A PERSON'S NAME (`preparedFor`), which is the first thing in here
 * that belongs to somebody who is not the agent. That is what `DRAFT_PREFIX` is for: see
 * `clearAllDrafts`. */
/** WHERE AN IN-PROGRESS REVIEW IS PARKED — now `./draftStore`, and now surviving the tab.
 *
 * This used to be two `sessionStorage` calls and a prefix sweep, written out right here. It moved
 * out on 2026-09-07 because the tab-scoped copy WAS the whole of the "we can no longer go back to
 * any step" regression: a finished brief opened from the Briefs list is a tab that never ran the
 * wizard, so the grid rehydrated empty, `hasReview` below went false, and the rail rendered five
 * nodes and no doors. `draftStore` writes the same draft durably as well, with an expiry and a
 * sweep; its module note carries the reasoning and the honest limit (one browser, one machine).
 *
 * The draft still holds property facts the agent is looking at and, since 2026-08-05, `preparedFor`
 * — a homeowner's name. Nothing else, and never a credential or session token. */
/** THE CONFIRMED PAYLOAD, EXACTLY AS IT WENT TO THE SERVER, MINUS THE ONE KEY THAT MAY DIFFER
 * (2026-08-07, owner request: "the ability to go back to any of the steps in the workflow to
 * regenerate the file with a different persona or make adjustments without rerunning the entire
 * workflow").
 *
 * `meta` here holds every key the submit sends EXCEPT `persona`, which `submitVoice` adds. That
 * split is not tidiness — it is the whole guarantee. The reconciled price is identical across
 * voices only because the payload is byte-identical except `meta.persona`, so the only safe way to
 * build a second voice is to hand the FIRST payload back unchanged and change that one key. There
 * is deliberately no code path that rebuilds `subject`/`comps` for a re-voice: the finished report's
 * `data.grid.comps` is a list of display names and formatted strings and cannot be turned back into
 * inputs, and re-deriving from the live grid would let an unrelated edit move the headline between
 * two files that claim to be the same analysis in two registers. */
interface ConfirmedSubmission {
  subject: Record<string, unknown>;
  comps: Record<string, unknown>[];
  tier: ReportTier;
  /** Every `meta` key except `persona`. `value_adjustments` travels in here untouched — the pool
   * overlay's baseline slides with the reconciled point, so dropping or retyping it would make the
   * headline and the grid disagree between two voices of the same analysis. */
  meta: Record<string, unknown>;
}

interface PersistedDraft {
  grid: SubjectGrid;
  comps: CandidateComp[];
  condition: string;
  addressLine: string;
  tier: ReportTier;
  persona?: PersonaId;
  personas?: PersonaId[];
  /** THE VOICES ACTUALLY BUILT, AND THEIR JOB IDS (2026-08-07). Absent from the draft until now,
   * which cost the agent the whole comparison set: a multi-voice run wrote five jobs, the URL
   * carried the first one, and a refresh restored an empty `personaJobs` — so the screen collapsed
   * from "your five reports" to voice 1's inline report and the other four became unreachable ids
   * nothing on the client still held. Absent on an older draft, which reads as "no siblings", i.e.
   * the single-voice path. */
  personaJobs?: { persona: PersonaId; jobId: string }[];
  /** VOICES OPENED IN ANOTHER TAB (2026-08-14, owner request).
   *
   * Held apart from `personaJobs` on purpose. `personaJobs.length > 1` is what swaps this screen
   * for the download set, and that swap is precisely what re-voicing must no longer do — see
   * `handleRevoice`. These jobs are real and the agent has them; they are simply not in THIS
   * tab's report. Persisted so a reload does not re-offer a voice the agent already spent a model
   * call on, and so the tab can still link to one whose window they have since closed. */
  revoicedAway?: { persona: PersonaId; jobId: string }[];
  /** THE EXTRACT JOB'S ID, HELD APART FROM `job` (2026-08-07). See `extractJobId` in the component:
   * `job` becomes the BUILD job the moment a report is generated, and the submit's `sourceJobId`
   * must keep naming the extract, or every server-verified comp photo is downgraded and stripped on
   * the second submit. Persisted so a refresh between two submits cannot lose it. */
  extractJobId?: string | null;
  /** THE CONFIRMED PAYLOAD (2026-08-07). Persisted so "rebuild in another voice" survives a
   * refresh: without it the control would have to either disappear after a reload or rebuild the
   * inputs, and rebuilding is exactly what must not happen. See `ConfirmedSubmission`. */
  confirmedPayload?: ConfirmedSubmission | null;
  /** Data-free: a payload that held MLS rows was deliberately not stored (`draftStore`). */
  confirmedPayloadWithheld?: boolean;
  /** THE SUBJECT ADDRESS THE COMPARABLE SET WAS READ FOR (2026-08-07). See `compsAnchor` in the
   * component. Persisted because the whole point of it is to survive a change to the grid, and a
   * refresh is a change to nothing — restoring the comps without the address they belong to would
   * re-arm the flow with no way to notice a subject that had moved. Absent on an older draft, which
   * reads as "no anchor recorded", i.e. no warning — the behaviour before this existed. */
  compsAnchor?: string | null;
  /** The non-comparable halves of the upload. Persisted too, or a refresh would silently drop the
   * agent's competition set and their subject's own prior sale from the review. */
  listings?: CandidateComp[];
  priorSales?: CandidateComp[];
  gaps?: string[];
  /** What THIS SCREEN told the agent about how the subject was reconciled, and the same fact as it
   * is said on step 2. Persisted for the same reason `gaps` is: a disclosure the agent has been
   * given must not disappear because they reloaded the tab. Absent on drafts written before this
   * existed, which reads as "nothing was reconciled", i.e. the old behaviour. */
  reconcileNotes?: string[];
  subjectNotice?: string | null;
  /** Step 2's two inputs (2026-08-01). Typed by a human and reproducible from nothing, so losing
   * them to a refresh costs more than losing an extracted field, which can at least be re-read. */
  valueAdjustments?: ValueAdjustments;
  /** The agent's market-conditions override (2026-08-10). Optional, so a draft written before this
   *  field existed restores exactly as it always did — absent means "use the rate the engine
   *  derives from the file's own closings", which is also the default for a fresh run. */
  marketConditions?: MarketConditions;
  extraContext?: string;
  /** The homeowner's name, if the agent chose to supply one on step 4 (2026-08-05). Typed by a
   * human like the two above, so it is persisted for the same reason they are. Absent on a draft
   * written before this existed, which reads as "no name was given" — the default. */
  preparedFor?: string;
  /** The subject exactly as the agent left step 1 (2026-08-05). Persisted so a refresh on step 2
   * still knows which rows are the ones they already confirmed and which are what the file added —
   * without it, a recovered draft would present every confirmed field as new. Absent on a draft
   * written before this existed, which reads as "no step 1 to diff against" and simply shows the
   * whole form, i.e. the old behaviour. */
  step1Grid?: SubjectGrid | null;
  /** THE LAST RESOLVE'S REPORTING (2026-08-11). The ladder, the outage channel, the gap list and
   * the label strings — everything `CascadeMeta` holds, and deliberately not the subject/comps it
   * already merged into `grid`/`comps` above.
   *
   * Persisted for the same reason `gaps` and `reconcileNotes` are: these are things the agent has
   * been TOLD, and a reload is not a reason to withdraw a disclosure. Losing it would also drop the
   * per-row provider test (`rung_labels`) and the "we could not ask" panel on a refresh, which
   * turns an outage into a screen that silently looks like a clean empty result. Absent on a draft
   * written before this existed, which reads as "no cascade ran" — the old behaviour. */
  cascade?: CascadeMeta | null;
  /** WHERE THEY WERE. The wizard has four steps now; dropping a returning agent at the first one
   * with all their work intact but no memory of how far they had got is its own small insult. Only
   * the reviewing steps are ever restored — never `build`, which belongs to the job's status. */
  step?: "detail" | "comps" | "review";
  /** The county record the subject is locked to, and the ledger of switches (2026-10-04). Absent
   * on an older draft, which reads as "no CAD record picked, nothing switched". */
  subjectCadPick?: CadPick | null;
  subjectChangeLog?: SubjectChangeEntry[];
}

/** THE SUBJECT GRID HELPERS MOVED to `./subjectGrid` (2026-08-04) so the address-first intake
 * screen, which now builds a grid before any file exists, uses the SAME definitions this flow does.
 * `gridFromExtractedSubject` is kept as a thin named wrapper because "Your uploaded file" is this
 * call site's honest source tag and spelling it out at every use invites the wrong one. */
function gridFromExtractedSubject(subject: Record<string, unknown>, tag = UPLOADED_FILE_TAG): SubjectGrid {
  return gridFromSubject(subject, tag);
}

/** A county appraisal sheet's own tag, as `amb_extract.cad_source_label` writes it. */
function isCadTag(tag: string | null | undefined): boolean {
  return !!tag && /(Appraisal District|county appraisal district) record$/i.test(tag);
}

/** Facts a county appraisal sheet never lists, whatever the county: the FORMAT has no place for
 * them. A blank one is the sheet's limit, not an absence in the world, and says so. */
const CAD_NEVER_LISTS = new Set(["hoa", "era", "neighborhood_name", "condition_code"]);

/** What a blank subject row says when the subject was read from a county appraisal sheet. */
function cadBlankNote(key: string): string {
  return CAD_NEVER_LISTS.has(key) ? "A county appraisal sheet doesn't list this" : "Not on the county sheet";
}

/** The tag a file-filled row carries when the server named no source for the file. */
const UPLOADED_FILE_TAG = "Your uploaded file";

/** The file's own named source when the server gave one (a CAD sheet's district), else the plain
 * upload tag. One helper for both places an extract lands in the grid, so they cannot disagree. */
function extractTag(grid: { subject_source?: string | null } | null | undefined): string {
  const named = typeof grid?.subject_source === "string" ? grid.subject_source.trim() : "";
  return named || UPLOADED_FILE_TAG;
}

/** An extract job's stage vocabulary vs. a build job's. Used only when rehydrating from a URL,
 * where the job id alone doesn't say which half of the flow the user was in. */
function looksLikeExtractStage(stage: string): boolean {
  return (EXTRACT_STAGES as readonly string[]).includes(stage) || stage === "extract" || stage === "queued";
}

/** Module-level so the prop identity is stable across renders. */
const ADDRESS_ONLY = ["address"];
/** Module-level for the same reason as `ADDRESS_ONLY`: a stable prop identity across renders. */
const ACRES_ONLY = ["acres"];

/** THE FOUR-STEP WIZARD (2026-08-01, owner directive). `detail` and `comps` are new; the other
 * three keep their old names. The reviewing sequence is detail -> comps -> review, and only those
 * three are ever restored from a draft. */
type Step = "intake" | "detail" | "comps" | "review" | "build";

/** The reviewing steps, in order — the single place the sequence is written down, so a Back button,
 * a draft restore and a step gate can never disagree about what comes next. */
/**
 * ARE THESE TWO STRINGS THE SAME ADDRESS, for the narrow purpose of deciding whether a resolved
 * Cotality clip may be reused (see `subjectClip`)?
 *
 * DELIBERATELY CONSERVATIVE AND DELIBERATELY NOT A PARSER. It lowercases, drops punctuation and
 * collapses whitespace, and that is all. It expands no abbreviations, reorders nothing, and knows
 * no USPS rules — because the cost of the two answers is not symmetric. A false NEGATIVE costs one
 * extra provider call, which is the behaviour that shipped for months. A false POSITIVE fetches the
 * WRONG PROPERTY and labels it with the address on screen, which is a fabricated record in a
 * document a licensed professional signs. So this returns false whenever it is unsure.
 *
 * It exists because the provider echoes its own casing and punctuation for an address the agent
 * typed — "4815 Springtree Ln." against "4815 SPRINGTREE LN" is the same property, and a raw
 * string comparison would throw the saving away on every typed-address lookup.
 *
 * Exported for `CmaAmbFlow.clip.test.tsx`, which is where the pairs that must and must not match
 * are written down.
 */
export function sameAddress(a: string, b: string): boolean {
  const norm = (v: string) =>
    String(v ?? "")
      .toLowerCase()
      .replace(/[.,#]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const na = norm(a);
  return na.length > 0 && na === norm(b);
}

const REVIEW_STEPS = ["detail", "comps", "review"] as const;
type ReviewStep = (typeof REVIEW_STEPS)[number];

function isReviewStep(s: unknown): s is ReviewStep {
  return typeof s === "string" && (REVIEW_STEPS as readonly string[]).includes(s);
}

export function CmaAmbFlow() {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId?: string }>();

  /* ------------------------------------------------------------------------------------------
   * WHICH VIEW OF THE REPORT, IN THE ADDRESS BAR (S3-02, 2026-09-05).
   *
   * `/cma/{id}?view=document` opens the document editor on the brief. Same screen, same route,
   * same component tree — a query parameter rather than a second surface, for the reason UI-009
   * put the job id in the URL in the first place: what the agent is looking at should survive a
   * reload, a bookmark and a link pasted to a colleague.
   *
   * WHY THE URL AND NOT `useState`. Three things fall out of it that state cannot give:
   *   · DEEP-LINKABLE. `/cma/{id}?view=document` is a real address, so "open my brief in the
   *     editor" is a link rather than a route plus two clicks.
   *   · BACK AND FORWARD WORK. `setSearchParams` pushes a history entry, so switching view is
   *     something the back button undoes — which is what a browser's back button means, and
   *     switching views is exactly the kind of thing people undo that way.
   *   · NO LOST WORK. Back out of the document view and the editor unmounts, which flushes its
   *     pending autosave (`editor/autosaveFlush.test.tsx`); come forward again and it reloads the
   *     brief and restores the stored document. The 700ms debounce window used to be a hole here
   *     and is not one now.
   *
   * `resolveView` is the only thing that turns the string into a view, so an unknown value — or
   * `document` while the flag is off — is the working view rather than a broken screen.
   * ------------------------------------------------------------------------------------------ */
  const [searchParams, setSearchParams] = useSearchParams();
  const reportView = resolveView(searchParams.get("view"));
  const setReportView = useCallback(
    (next: ReportView) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          /* THE DEFAULT IS THE ABSENCE OF THE PARAMETER, not `?view=working`. A URL that grows a
             parameter the first time somebody looks at a toggle and never loses it is a URL people
             paste with state in it they did not mean to share. */
          if (next === "working") params.delete("view");
          else params.set("view", next);
          return params;
        },
        /* PUSH, NOT REPLACE. The whole point is that back undoes the switch. */
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const [step, setStep] = useState<Step>("intake");
  /** §6 (gap closed 2026-10-02): the SERVER's own `limit_reached` sentence, set by whichever of
   * `runCascade` / `readFiles` / `handleFetchPublicRecords` / `handleAddFileMerge` / `handleSubmit`
   * last got a 429 back. Proactive disabling (driven by the shared `/api/me/usage` cache) covers
   * the ordinary case; this covers the RACE — two tabs at 9 of 10, where the button a user clicked
   * was genuinely enabled and the server is what actually refused it. A refused click that shows
   * nothing is a dead control.
   *
   * ONE SHARED SLOT, CLEARED ON EVERY STEP CHANGE. The five handlers above are reachable from up
   * to three different screens, and the wizard renders exactly one step's screen at a time — so a
   * single slot, wiped the moment `step` changes, can never leak a step-2 refusal onto step 4
   * without a second piece of state per screen. */
  const [limitRefusal, setLimitRefusal] = useState<string | null>(null);
  useEffect(() => {
    setLimitRefusal(null);
  }, [step]);
  const [job, setJob] = useState<AmbJobPublic | null>(null);
  /** THE EXTRACT JOB'S ID, IN ITS OWN STATE (2026-08-07, found while making a second submit
   * possible).
   *
   * `job` is whichever job the flow is currently FOLLOWING, and `handleSubmit` reassigns it to the
   * build job the moment a report starts. The submit's `sourceJobId` is a different question — it
   * names the extract the payload was reviewed from, and it is the only proof
   * `_reconcile_client_photo_status` accepts for a comp photo the server itself verified.
   *
   * Sending `job?.id` was correct exactly once. On any SECOND submit — a re-voice, or a Generate
   * pressed again after going back to review — it sent the BUILD job's id, whose `data_json` is the
   * finished report dict with no top-level `comps` key at all. The lookup therefore proves nothing,
   * `server_verified` comes back empty, and every verified comp photo is silently downgraded to
   * "unresolved" and stripped: the second report comes back without the photos the first one had,
   * with nothing on screen to say why. Held here so the id cannot drift with `job`. */
  const [extractJobId, setExtractJobId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [phase, setPhase] = useState<ExtractPhase>("idle");
  // UI-088: one `error` string was shared by the extract, the public-records fetch, the added-file
  // merge, and the submit — so a stale extract failure could still be sitting on the review screen
  // while the real problem was the submit, and vice versa.
  const [extractError, setExtractError] = useState<string | null>(null);
  /* THE FILE WE CHOSE NOT TO READ (2026-09-22, owner direction). Held IN MEMORY ONLY, and on
     purpose: `File` objects cannot be serialized into the draft, so after a refresh there is
     nothing the "Use my file instead" button could read — and a button that cannot act must not
     render. Null whenever there is no skipped file to offer. */
  const [skippedUpload, setSkippedUpload] = useState<{
    files: File[]; addressLine: string; localReadOnly?: boolean;
  } | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null); // UI-069: non-error status (e.g. a successful fallback fetch)
  const [stalled, setStalled] = useState(false);

  const [fileNames, setFileNames] = useState<string[]>([]);
  const [addressLine, setAddressLine] = useState("");
  /** THE SUBJECT'S RESOLVED COTALITY RECORD (2026-09-01, T-5), lifted out of `SubjectAddressStep`.
   *
   * Step 1 already resolves a clip — from a picked type-ahead suggestion, or from the lookup's own
   * response — and step 2's "Fetch public records" then looked the same property up AGAIN by
   * address, missing the clip-keyed cache: a `search`, a `typeahead` and a `property-detail`, which
   * `tests/test_cotality_report_call_census.py` prices at 3 outbound calls and 1 cap unit per
   * report.
   *
   * THE ADDRESS IS STORED WITH IT because a clip identifies a PROPERTY and the agent can edit the
   * address afterwards, here or in the grid. Using a clip whose address no longer matches would
   * fetch the previous property and label it with the current address, which is a wrong record
   * rather than a slow one. `subjectClipFor()` is the only reader and it checks. */
  const [subjectClip, setSubjectClip] = useState<{ clip: string; address: string } | null>(null);
  /** THE COUNTY CAD RECORD THE SUBJECT IS LOCKED TO (2026-10-04, slice data-cad-picker), by
   * `prop_id`, the way `subjectClip` locks a Cotality record. Lifted from `SubjectAddressStep` so
   * the chosen record and its "Change" control survive that screen unmounting and a reload. */
  const [subjectCadPick, setSubjectCadPick] = useState<CadPick | null>(null);
  /** The pick as of the last commit, for the cascade calls that run after an await (the gap-fill
   * after a file read). Every cascade request reads its lock from here — see `runCascade`. */
  const cadPickRef = useRef<CadPick | null>(null);
  useEffect(() => {
    cadPickRef.current = subjectCadPick;
  }, [subjectCadPick]);
  /** THE SUBJECT CHANGE LEDGER (owner design, 2026-10-04): one entry per confirmed switch of
   * record — from prop_id/dataset_version, to prop_id/dataset_version, at. Saved with the review
   * and sent with Generate as `meta.subject_change_log`. WHO is stamped by the server, never here. */
  const [subjectChangeLog, setSubjectChangeLog] = useState<SubjectChangeEntry[]>([]);
  /** What stands in place of "Fetch from public records" while the subject is a picked county
   * record: where it came from, with its dataset version, and why nothing is searched. */
  const lockedRecordNote = subjectCadPick
    ? `This subject is the ${cadRecordTag(subjectCadPick)} county record you picked, so public ` +
      "records are not searched for it. To use a different property, change it on the first step."
    : null;
  const logSubjectChange = useCallback(
    (entry: SubjectChangeEntry) => setSubjectChangeLog((log) => [...log, entry]),
    []
  );
  const [grid, setGrid] = useState<SubjectGrid>({});
  /** STEP 1'S OWN STATE, HELD HERE (2026-08-05, owner: "the tool makes us confirm the subject file
   * multiple times"). The file list, the photo choice and the photo disclosures used to be local to
   * `IntakeScreen`, which is mounted on step 1 and UNMOUNTED the moment the extract lands. Pressing
   * "Back to your file" on step 2 therefore destroyed all of it and returned an empty address box
   * on a flow whose next screen was showing a fully populated subject; the agent had to retype the
   * address and redo the lookup to get back to where they already were.
   *
   * `addressLine` and `grid` above are the other half of the same fix: step 1 now edits THE FLOW's
   * address and THE FLOW's subject grid directly, so steps 1 and 2 are one continuous form over one
   * piece of state rather than two forms that happen to start out equal. */
  const [intake, setIntake] = useState<IntakeState>(EMPTY_INTAKE);
  /** THE SUBJECT AS THE AGENT LEFT STEP 1 — the frozen line between "you already confirmed this"
   * and "this is new". Null when step 1 never produced a subject (no lookup, no typing), in which
   * case step 2 is the FIRST time these fields are asked for and asks for all of them.
   *
   * Snapshotted at submit rather than tracked live, because the question step 2 answers is "what
   * has changed since you pressed the button", and the answer has to stop moving while they read
   * it. See `carryOver` in `subjectGrid.ts`. */
  const [step1Grid, setStep1Grid] = useState<SubjectGrid | null>(null);
  /** A fact about the upload the agent has to be told on step 2, said in place rather than only in
   * step 3's reading notes. Currently one case: the sheet is a record for a different property than
   * the one they confirmed, so none of its subject detail was used.
   *
   * RENDERED INDEPENDENTLY OF `step1Grid` (2026-08-05, review finding). It used to reach the screen
   * only as a prop of `SubjectCarryOver`, which is mounted inside `{step1Grid && …}` — so on any
   * path without a step 1 grid (a restored draft, an agent who skipped the lookup) the notice was
   * computed, stored, and shown to nobody. A disclosure that renders only in some states is not a
   * disclosure. It has its own block on step 2 now, and that block is not behind `step1Grid`. */
  const [subjectNotice, setSubjectNotice] = useState<string | null>(null);
  const [condition, setCondition] = useState("average");
  const [comps, setComps] = useState<CandidateComp[]>([]);
  // The two things in an upload that are NOT comparable sales, kept in their own state so they can
  // never be submitted as comps by accident.
  const [listings, setListings] = useState<CandidateComp[]>([]);
  /** The competing-homes search's view of `listings` (hidden rows, order, reasons). Memory only. */
  const [listingsView, setListingsView] = useState<ActivesView>({ hidden: new Set(), order: undefined, reasons: {} });
  const [priorSales, setPriorSales] = useState<CandidateComp[]>([]);
  /** THE SUBJECT ADDRESS THE COMPARABLE SET ON SCREEN WAS ASSEMBLED FOR (2026-08-07, the other half
   * of the step rail becoming navigation).
   *
   * Recorded at the moment the comps arrive and never re-derived from the grid afterwards — which
   * is the entire point. Once a breadcrumb can take the agent from a finished report back to step 1,
   * the grid's address is a thing that MOVES, and a comparable set that was read for the previous
   * home is still ticked and still feeding the estimate when it does. Comparing the live grid
   * against itself would always agree; comparing it against this cannot.
   *
   * Null until an extract has actually landed comps, which reads as "there is nothing downstream",
   * i.e. no warning. See `EditImpact.describeSubjectChange` for what the comparison then decides. */
  const [compsAnchor, setCompsAnchor] = useState<string | null>(null);
  const [gaps, setGaps] = useState<string[]>([]);
  /** THE DISCLOSURES THIS SCREEN WROTE ITSELF, HELD APART FROM THE SERVER'S (2026-08-05, review
   * finding). `gaps` above is the SERVER's list and `applyExtract` rightly replaces it wholesale
   * every time an extract lands. Client-authored notes about how the subject was reconciled used to
   * be pushed into that same array a line or two BEFORE `applyExtract` ran, so every one of them was
   * overwritten in the same React batch and never reached the reading notes at all — including the
   * comps-only "nothing was borrowed from a comparable" note, which is the disclosure that justifies
   * not asking in that case.
   *
   * Kept as its own array so no future `setGaps([...])` can destroy it. Rendered ahead of the
   * server's notes in `ReadingNotes`, and persisted with the draft like every other thing the agent
   * has been told. NOTE: like `gaps`, these are a SCREEN surface — nothing on the client sends
   * either list to the build, so neither appears in the deliverable. */
  const [reconcileNotes, setReconcileNotes] = useState<string[]>([]);
  /** Append-once, so a rehydrate that re-runs terminal handling cannot say the same thing twice. */
  const addReconcileNote = useCallback((note: string) => {
    setReconcileNotes((n) => (n.includes(note) ? n : [note, ...n]));
  }, []);
  /** HOW EACH FILE WAS READ (2026-08-04). `grid.files` has been on the payload all along; nothing
   * on the client had a name for it, so the third-party-OCR disclosure it carries — a stated
   * product requirement — reached nobody. See `FileReadReceipt`. */
  const [fileStatus, setFileStatus] = useState<FileReadStatus[]>([]);
  /** The engine's closed-comp floor as the SERVER reports it (`ExtractGrid.min_comps`). Held in
   * state rather than re-declared as a client constant, so there is no third copy of
   * MIN_ARMS_LENGTH_COMPS to keep in lockstep with Python. */
  const [minComps, setMinComps] = useState<number | undefined>(undefined);
  const [mismatch, setMismatch] = useState<AddressMismatch | null>(null);
  /** Whether the agent already holds a subject worth keeping — any confirmed field beyond the
   * address itself. A ref rather than a dep, because `onExtractTerminal` is a stable callback and
   * reading state through a closure there would test whatever the grid was when it was created. */
  const confirmedSubjectRef = useRef(false);
  /** The subject address the human has actually CONFIRMED, which is the provider's normalized form
   * whenever step 1 ran a lookup ("4815 Springtree Ln, Wylie, TX 75098") and not the shorthand they
   * typed into the box ("4815 springtree wylie"). GATE-004 used to compare the typed string against
   * the file's, i.e. the weaker of the two strings the app is holding, and fired on ordinary jobs
   * because of it. A ref for the same reason `confirmedSubjectRef` is one: `onExtractTerminal` is a
   * stable callback and reading `grid` through its closure tests whatever the grid was when the
   * callback was created. */
  const confirmedAddressRef = useRef("");
  useEffect(() => {
    confirmedSubjectRef.current = Object.entries(grid).some(
      ([key, f]) => key !== "address" && f?.checked && f.value !== null && f.value !== undefined && f.value !== ""
    );
    confirmedAddressRef.current = isFieldConfirmed(grid, "address")
      ? String(grid.address.value).trim()
      : "";
  }, [grid]);
  /* ------------------------------------------------------------------------------------------ *
   * THE SOURCE CASCADE (2026-08-11 — `docs/product/SOURCE-CASCADE-SPEC.md`, server half in
   * `src/server/source_cascade.py`). Read the spec before changing any of this.
   *
   * WHAT IT IS FOR, in one sentence: a CMA that does not require an MLS export, and therefore does
   * not require MLS membership. The agent types an address; two ladders run server-side (address ->
   * public record -> agent for the subject; MLS -> uploaded files -> public records -> agent for
   * the comparables); everything comes back in the engine shape this flow already speaks.
   *
   * WHAT IT IS NOT: a feature, a mode, or a choice. There is no source picker anywhere in this
   * file and there must not be one. The cascade is machinery — spec §4 — and the agent's only
   * inputs are an address and, if they happen to have one, a file.
   * ------------------------------------------------------------------------------------------ */
  /** Gates read once on mount from `/api/next/cascade/status` — NO provider call, NO daily-cap
   * spend, which is exactly why the "this is not real data" notice can be on screen 1 before the
   * agent has typed anything. Null until it answers, and a failure leaves it null: the flow works
   * without it, it simply has nothing to disclose. */
  const [cascadeStatus, setCascadeStatus] = useState<CascadeStatus | null>(null);
  // Is the MLS access screen switched on? Only then may the no-comps card offer "Request MLS access".
  const mlsOffered = useMlsOffered();
  /** The reporting half of the last resolve — which rung answered, what could not be ASKED, what is
   * still missing, the label strings. Deliberately NOT the subject and comps: those are consumed
   * into `grid` and `comps` the moment they arrive, and a second copy of the facts the agent is
   * editing is a second copy that can disagree. See `CascadeMeta`. */
  const [cascade, setCascade] = useState<CascadeMeta | null>(null);
  const [resolving, setResolving] = useState(false);
  /** UI-088's rule again: the cascade gets its own error string rather than borrowing the
   * extract's, so a failed lookup cannot sit on a screen about a file that read perfectly. */
  const [cascadeError, setCascadeError] = useState<string | null>(null);
  /** The last resolve's arguments, so "Try again" on the outage panel re-asks the SAME question
   * rather than a rebuilt one. A ref, not state: nothing renders from it. */
  const lastResolveArgs = useRef<CascadeResolveArgs | null>(null);
  // COMP SEARCH (1G). `compSearchOpen` is the one switch for the "Find comparable sales" options;
  // anything that needs them (the no-comps card's [Adjust search]) calls `setCompSearchOpen(true)`.
  // `compActive` is the comp the map and the rows are both pointing at; `compShownOrder` is the
  // grid's own display order, which is what numbers the pins.
  // OPEN BESIDE THE MAP ON A WIDE SCREEN (comps-ux item 2): closed, the options column is a blank
  // slab next to a 560 px square map. From 1200 px up the options start open; below that, and on a
  // phone, they stay behind "Adjust search" so the map and the sales are what is on screen.
  const [compSearchOpen, setCompSearchOpen] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(min-width: 1200px)").matches);
  // The MLS sales the last pull fetched and the criteria set aside (comps-ux). Memory only. A held
  // row that a filter brings into `comps` carries `_held_mls`; `saveDraft` strips such rows from
  // every copy (session, durable, server) and the wire strips the mark, so none is ever saved.
  const [heldBack, setHeldBack] = useState<CandidateComp[]>([]);
  const [compSearchFocusTick, setCompSearchFocusTick] = useState(0);
  const [compActive, setCompActive] = useState<number | null>(null);
  const [compShownOrder, setCompShownOrder] = useState<number[]>([]);
  /* "BEST MATCH" (C1, 2026-11-02). The engine's own order of the sales on screen (`rank_comps`,
     asked of `POST /api/next/comp-search/rank` — the client never ranks), as indices into `comps`.
     Recomputed whenever the set or a ranking field changes; memory only, because it is a pure
     function of what is saved. `bestNote` is why it is not available, said beside Order by. */
  const [bestOrder, setBestOrder] = useState<number[] | null>(null);
  const [bestNote, setBestNote] = useState<string | null>(null);
  /** The comp sets whose top three have already been ticked once. A set the agent then unticks is
   * left alone: the start is a suggestion, made once, never re-imposed. */
  const bestPrechecked = useRef<Set<string>>(new Set());

  // Gates up front. Cheap, uncapped, and the sandbox banner depends on it being here before the
  // first keystroke rather than after the first lookup.
  useEffect(() => {
    let cancelled = false;
    getCascadeStatus()
      .then((s) => {
        if (!cancelled) setCascadeStatus(s);
      })
      .catch(() => {
        /* Non-fatal by design: an older deployment without the router simply has no gates to
           report, and the flow behaves exactly as it did before the cascade existed. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [fetchingPublicRecords, setFetchingPublicRecords] = useState(false);
  const [merging, setMerging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadedCounties, setLoadedCounties] = useState<LoadedCounty[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<LoadedCounty | null>(null);
  // THE TIER CHOSEN IN THE NEW BRIEF DIALOG ARRIVES IN THE ADDRESS (2026-09-25) and is honored;
  // with none, AMB stays the default, as it always has been.
  const [tier, setTier] = useState<ReportTier>(() => tierFromParam(searchParams.get("tier")) ?? "amb");
  /* PERSONAS, PLURAL (2026-08-04, owner directive). One voice is still the default and behaves
     exactly as it always has — one job, one inline report. Selecting more than one builds a
     SEPARATE job per voice from the same confirmed inputs, which is the structural answer to the
     owner's stated worry about cross contamination: each voice gets its own `AmbJob` and its own
     `data_json`, written by its own build, so there is no code path by which one voice's prose
     could reach another's file. */
  const [personas, setPersonas] = useState<PersonaId[]>([DEFAULT_PERSONA]);
  /** One entry per voice actually submitted. A single-voice run holds exactly one, and the download
   * screen renders only above one — so the ordinary path is unchanged. Persisted with the draft
   * since 2026-08-07; see `PersistedDraft.personaJobs` for what losing it cost. */
  const [personaJobs, setPersonaJobs] = useState<{ persona: PersonaId; jobId: string }[]>([]);
  /** THE PAYLOAD THE AGENT CONFIRMED, KEPT SO ANOTHER VOICE COSTS ONE MODEL CALL AND NOTHING ELSE
   * (2026-08-07, owner request). Null until a Generate has actually been pressed. See
   * `ConfirmedSubmission` for why this is stored rather than rebuilt. */
  const [confirmedPayload, setConfirmedPayload] = useState<ConfirmedSubmission | null>(null);
  // Restored from the draft: the payload that built this report held MLS rows and was not stored.
  const [payloadWithheld, setPayloadWithheld] = useState(false);
  /** The voice currently being rebuilt from the finished-report screen, or null. Held as the id
   * rather than a boolean so the button the agent pressed is the one that says what it is doing. */
  const [revoicing, setRevoicing] = useState<PersonaId | null>(null);
  /** UI-088's rule, applied to the re-voice: its own error string. Folding it into `submitError`
   * would put a failed re-voice's message on the build progress card of a job that is fine. */
  const [revoiceError, setRevoiceError] = useState<string | null>(null);
  /** Voices opened into their own tab — see `handleRevoice` and `PersistedDraft.revoicedAway`. */
  const [revoicedAway, setRevoicedAway] = useState<{ persona: PersonaId; jobId: string }[]>([]);
  /** Set only when the browser refused the new tab. Carries the link so the report is reachable. */
  const [revoiceBlocked, setRevoiceBlocked] =
    useState<{ persona: PersonaId; jobId: string; href: string } | null>(null);
  /** WHERE THE AGENT ASKED TO GO, HELD WHILE THEY ANSWER FOR IT (2026-08-07). Non-null only while
   * the invalidation prompt is on screen: a forward move was requested, it would discard comparable
   * work, and nothing has been discarded yet. Nothing is cleared until the answer comes back — a
   * prompt that has already done the thing it is asking about is a notification, not a question. */
  const [pendingStep, setPendingStep] = useState<Step | null>(null);
  /** STEP 2'S TWO INPUTS (2026-08-01, owner decision: two, not one).
   *
   * `valueAdjustments` carries dollar figures and enters the math — as a disclosed, post-
   * reconciliation overlay attributed to the agent, never folded into the comparable grid.
   * `extraContext` carries prose and reaches the narrative drafter under a standing instruction
   * that it may not introduce a number or a fact the evidence doesn't already contain.
   *
   * Kept as two pieces of state for the same reason they are two boxes on the screen: the moment a
   * single field can become either, something has to decide which, and a dollar figure produced by
   * that decision has no source to trace to. See `UpgradesPanel`'s docstring. */
  const [valueAdjustments, setValueAdjustments] = useState<ValueAdjustments>({});
  /** THE TIME ADJUSTMENT'S AGENT OVERRIDE (2026-08-10). Held beside `valueAdjustments` and not
   *  inside it, because they are different kinds of fact: a value adjustment is a dollar figure
   *  about THIS home, while this is a rate about the market every comp sold into. The engine
   *  derives the rate from the file's own closings when it can (`engine.amb.resolve_market_trend`)
   *  and this only ever overrides that — an empty object means "use what the sales say". */
  const [marketConditions, setMarketConditions] = useState<MarketConditions>({});
  const [extraContext, setExtraContext] = useState("");
  /** THE HOMEOWNER'S NAME, SUPPLIED OR NOT AT ALL (2026-08-05, owner decision).
   *
   * Asked once, on step 4, next to the other choices about how the brief is presented. It travels
   * as `meta.prepared_for` and ONLY when the agent typed something: blank sends no key, the server
   * keeps its "Property owner" default, and nothing about the generated report changes. Empty is
   * both the default and the common case. See `HomeownerName`. */
  const [preparedFor, setPreparedFor] = useState("");
  // The CAD sheet's own identity facts, from the extract that read it (2026-09-25).
  const [cadIdentity, setCadIdentity] = useState<{ owner_name?: string; city?: string; county?: string; locality?: string } | null>(null);
  // THE CONFIRMED SUBJECT PHOTO (2026-09-25, owner P0). What the agent uploaded in review or on the
  // finished report; it prints, and it outranks the photo the extract read. `extractPhoto` is that
  // read photo, shown so the agent can see what will print before replacing it.
  const [confirmedSubjectPhoto, setConfirmedSubjectPhoto] = useState<string | null>(null);
  // THE PHOTO PICKER (2026-11-03). Held here, not in the step, so the agent's choice survives steps 1 to 4
  // and the finished report. null = the server's default stands and nothing extra is sent: a build with
  // the default selected is byte-identical to one that never saw the picker.
  const [photoChoiceKey, setPhotoChoiceKey] = useState<string | null>(null);
  // Owner decision 7: the owner of record may print on "Prepared for" -- only once the agent ticks it.
  const [useOwnerOfRecord, setUseOwnerOfRecord] = useState(false);
  const [extractPhoto, setExtractPhoto] = useState<{ photo: string; source: string | null } | null>(null);
  async function choosePhotoOption(p: SubjectPhotoOption, isDefault: boolean) {
    if (isDefault) { setConfirmedSubjectPhoto(null); setPhotoChoiceKey(null); return; }
    const uri = await photoOptionToDataUri(p);
    setConfirmedSubjectPhoto(uri);
    setPhotoChoiceKey(photoKey(p));
  }

  /** THE CONFIRMED SUBJECT ADDRESS (2026-07-31). `addressLine` is only what the agent TYPED on the
   * intake screen. When the subject is resolved from the county/Cotality lookup, or read out of the
   * uploaded file, `addressLine` is still "" — and every consumer that reached for it got nothing:
   *
   *   * `handleAddFileMerge` sent it to `/api/amb/extract`, so `split_subject_prior_sale` was handed
   *     "" and returned on its first line. The "a home is never its own comparable" guard turned
   *     ITSELF off on the add-a-sheet path — the exact door the owner reproduced the subject's own
   *     prior listing coming back through.
   *   * the confirmed-submit `meta` omitted `address_line` entirely, so `build_data` fell back to
   *     its literal "Subject property" placeholder and the deliverable's Street View subject photo
   *     was fetched against that string, which of course has no imagery. Comp photos still filled
   *     (they key off each card's own name), which is why the report came back with comp photos and
   *     an empty hero.
   *
   * The grid's `address` is the value the human confirmed, so it outranks the typed line; the typed
   * line stays as the fallback for the pre-review window, where no grid exists yet. */
  const subjectAnchor = String(grid.address?.value ?? "").trim() || addressLine.trim();
  // THE COUNTY SHEET, IF THE SUBJECT CAME FROM ONE -- read off the rows' own tags, so it survives a
  // refresh with the grid instead of living in a second piece of state that could disagree.
  const cadSource = Object.values(grid).map((f) => f?.sourceTag).find(isCadTag) ?? null;

  // VB-39: the one screen where a per-brief tab name is both possible and worth having — an agent
  // runs several of these and comes back to them. `subjectAnchor` is the CONFIRMED address when
  // there is one and the typed line otherwise, so the tab names itself as soon as step 1 resolves
  // and reads "New analysis" before that. Display only; nothing derives from it.
  useDocumentTitle(subjectAnchor ? vbTitle(subjectAnchor) : vbTitle("New analysis"));

  /** The review map's pins, derived from what the agent is looking at RIGHT NOW — the confirmed
   * subject address, the comps they have ticked, and the competing set. Recomputed on every
   * change rather than read from the extract payload, because a map that still shows a comp the
   * agent just unticked is a map that disagrees with the number underneath it.
   *
   * Only SELECTED comps are plotted, for the same reason: the map's job is to show where the
   * value came from. `buildPins` holds the pin-or-don't rule — see `addressPin.ts`, and keep it in
   * lockstep with the Python twin that decides the same thing for the deliverable. */
  // WHAT COULD NOT BE PINNED IS PART OF THE MAP (2026-08-10). This read `.pins` and dropped the
  // rest of `buildPins`'s answer on the floor — so an agent with five selected comps and three
  // pinnable ones saw three pins and no signal that two were missing. A map that quietly omits a
  // comp tells the agent something false about where the value came from, which is the one thing
  // `geocode.py` refuses to do on the server side. `LocationMap` names them underneath.
  // THE SUBJECT'S LOCALITY, READ OFF ITS COUNTY SHEET (2026-09-25, Lane 5). A CAD's own address line
  // is a bare street ("2924 ROSEDALE AVE"), which neither this map nor the report's will pin -- so
  // the review listed the home as "Not mapped" and the report promised an H pin it did not draw.
  // `locality` is the sheet's taxing city, TX, and the ZIP only when the owner's mailing address is
  // the home itself (`amb_extract.cad_sheet_facts`). Nothing else supplies one on this path.
  const subjectLocality = cadSource ? (cadIdentity?.locality ?? "").trim() : "";
  // What a blank subject row says. A picked county record's own why-blank sentence comes first
  // (2026-10-18: "Dallas CAD lists a land area of 7,004 but does not state the unit…"); then the
  // county-sheet wording; else the grid's own "Nothing on record".
  const pickNotes = subjectCadPick?.fieldNotes;
  const subjectBlankNote = (pickNotes || cadSource)
    ? (key: string) => pickNotes?.[key] ?? (cadSource ? cadBlankNote(key) : undefined)
    : undefined;
  // The calm no-comps card (owner, 2026-10-19): a resolve came back, nothing could not be asked,
  // no sale is on the grid, and this viewer is not on the provider-diagnostics allow-list.
  const showNoCompsCard = !!cascade && !resolving && comps.length === 0
    && couldNotAsk(cascade).length === 0 && cascadeStatus?.provider_diagnostics !== true;
  const subjectPinQuery = subjectAnchor && subjectLocality && !subjectAnchor.includes(",")
    ? `${subjectAnchor}, ${subjectLocality}`
    : subjectAnchor;
  // Street View is the only option main can serve without #121; it is built from this address.
  const { options: photoOptions, retry: retryPhotoOptions } = useSubjectPhotoOptions(extractJobId, subjectPinQuery);
  const mapResult = buildPins([
    { address: subjectPinQuery, kind: "subject" as const },
    // B2 (2026-11-02): each sale carries its STORED location — the very coordinate the comps map
    // pins (`storedLocation`) — so step 3 and step 4 cannot disagree about where a sale is. Same
    // for the competing listings.
    ...comps.filter((c) => c._selected).map((c) => ({
      address: compMapQuery(c), kind: "comp" as const, at: storedLocation(c), refused: serverRefusedLocation(c) })),
    ...listings.map((c) => ({
      address: compMapQuery(c), kind: "competing" as const, at: storedLocation(c), refused: serverRefusedLocation(c), status: c.status })),
  ]);
  const mapPins = mapResult.pins;

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // The added-file merge runs its own poll. It used to be a bare local `setInterval`: nothing could
  // cancel it, so a wedged follow-up job left "Generate" disabled forever, and a Start-over mid-
  // merge could be silently un-done ~20s later when the orphaned poll wrote the discarded job's
  // rows back into the fresh grid.
  const mergeTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const mergeAbandoned = useRef(false);
  const addFileInputRef = useRef<HTMLInputElement>(null);
  const rehydratedFor = useRef<string | null>(null);

  // Loaded once, up front — the real, currently-loaded county datasets (never an aspirational
  // statewide list). Not gated behind the review step: fetching this is cheap and lets the picker
  // be ready the moment the readiness gate can show it.
  useEffect(() => {
    let cancelled = false;
    getLoadedCounties()
      .then((counties) => {
        if (cancelled) return;
        setLoadedCounties(counties);
        setSelectedCounty((prev) => prev ?? counties[0] ?? null);
      })
      .catch(() => {
        /* Non-fatal: the readiness gate degrades to upload/enter-only when this is empty. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const stopMerge = useCallback(() => {
    mergeAbandoned.current = true;
    if (mergeTimer.current) {
      clearInterval(mergeTimer.current);
      mergeTimer.current = null;
    }
    setMerging(false);
  }, []);

  useEffect(
    () => () => {
      stopPolling();
      stopMerge();
    },
    [stopPolling, stopMerge]
  );

  // UI-009: persist the in-progress review so a refresh, an accidental Back, or a closed laptop
  // doesn't throw away work the user already did on this screen.
  useEffect(() => {
    if (!job?.id) return;
    // Saved from the moment a job exists, not only at the review step. The typed subject address
    // lives ONLY here, and GATE-004 cannot ask "which one is the subject?" if a refresh mid-extract
    // has already lost the side of the question the human supplied.
    if (!isReviewStep(step) && phase === "idle" && !Object.keys(grid).length) return;
    saveDraft(job.id, {
      grid, comps, condition, addressLine, tier, personas, listings, priorSales, gaps,
      reconcileNotes, subjectNotice, valueAdjustments, marketConditions, extraContext, preparedFor, step1Grid,
      personaJobs, revoicedAway, extractJobId, confirmedPayload, compsAnchor, cascade,
      ...(payloadWithheld && !confirmedPayload ? { confirmedPayloadWithheld: true } : {}),
      subjectCadPick, subjectChangeLog,
      ...(isReviewStep(step) ? { step } : {}),
    });
  }, [job?.id, step, phase, grid, comps, condition, addressLine, tier, personas, listings, priorSales,
      gaps, reconcileNotes, subjectNotice, valueAdjustments, marketConditions, extraContext, preparedFor, step1Grid,
      personaJobs, revoicedAway, extractJobId, confirmedPayload, compsAnchor, cascade,
      payloadWithheld, subjectCadPick, subjectChangeLog]);

  // C1: ask the engine for the Best match order of the sales on the comps step. Debounced, and only
  // the fields `rank_comps` reads (plus the eligibility flags) are sent: no photo, no override trail.
  const bestKey = step === "comps" ? JSON.stringify([
    comps.map((c) => [c.name, c.price, c.gla, c.lot_sf, c.year, c.sale_type, c.status, c.sale_date,
                      c.distance, c.subdivision, (c as unknown as Record<string, unknown>).condition_code,
                      c.needs_fields, c.price_needs_input]),
    numFromGrid(grid.gla?.value), numFromGrid(grid.lot_sf?.value), numFromGrid(grid.year?.value),
    String(grid.subdivision?.value ?? ""), String(grid.condition_code?.value ?? ""),
  ]) : "";
  useEffect(() => {
    if (!bestKey) return;
    if (comps.length === 0) { setBestOrder([]); setBestNote(null); return; }
    let cancelled = false;
    setBestOrder(null);
    setBestNote(null);
    const rows = comps.map((c) => {
      const r = c as unknown as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const k of ["name", "price", "gla", "lot_sf", "year", "sale_type", "status", "sale_date",
                       "distance", "subdivision", "condition_code", "needs_fields", "price_needs_input"]) {
        if (r[k] !== undefined) out[k] = r[k];
      }
      return out;
    });
    const subjectFacts: Record<string, unknown> = {
      gla: numFromGrid(grid.gla?.value), lot_sf: numFromGrid(grid.lot_sf?.value),
      year: numFromGrid(grid.year?.value),
      subdivision: String(grid.subdivision?.value ?? "").trim() || null,
      condition_code: String(grid.condition_code?.value ?? "").trim() || null,
    };
    const setKey = comps.map((c) => normalizeAddress(c.name)).join("|");
    const t = setTimeout(() => {
      // `Promise.resolve().then` so a failure of any kind (thrown or rejected) lands in `.catch`.
      Promise.resolve().then(() => rankComps({ subject: subjectFacts, rows }))
        .then((res) => {
          if (cancelled) return;
          setBestOrder(res.order);
          // TICK THE TOP THREE ONCE, ON A SET NOTHING IS TICKED IN. Only rows the server named as able
          // to price a report AND that this screen can tick (no field still to fill).
          if (bestPrechecked.current.has(setKey)) return;
          bestPrechecked.current.add(setKey);
          setComps((cs) => tickTopRecommended(cs, res.recommended, rows.length));
        })
        .catch(() => {
          if (!cancelled) setBestNote("Best match isn't available right now.");
        });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [bestKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollJob = useCallback(
    (
      id: string,
      onTerminal: (j: AmbJobPublic) => void,
      terminalStatuses: AmbJobPublic["status"][],
      /** Where a lost connection is reported. The build poll and the extract poll are watched on
       * two different screens; writing both into the extract error string put the message on a
       * screen the user had already left, so a single 502 mid-build froze the progress card
       * forever with nothing rendered to explain it. */
      onLostContact?: (message: string) => void
    ) => {
      stopPolling();
      setStalled(false);
      let lastSignature = "";
      let sameCount = 0;
      pollTimer.current = setInterval(async () => {
        try {
          const j = await getJob(id);
          setJob(j);
          // UI-101 stall guard: a job that reports the same status+stage+OCR page for 90 seconds is
          // not "still working" in any way the user can act on. Say so instead of spinning forever.
          const signature = `${j.status}|${j.stage}|${j.ocr?.page ?? ""}|${j.progress ?? ""}`;
          sameCount = signature === lastSignature ? sameCount + 1 : 0;
          lastSignature = signature;
          setStalled(sameCount >= STALL_TICKS);
          if (terminalStatuses.includes(j.status)) {
            stopPolling();
            onTerminal(j);
          }
        } catch {
          stopPolling();
          // UI-011: the old string was "Lost contact with the job — try again," on a screen with
          // nothing to click. This one names what survived and what to actually do.
          const message =
            "We lost contact with the server while your file was being read. The file is still uploaded — reload this page and we'll pick the job back up.";
          if (onLostContact) {
            onLostContact(message);
          } else {
            setPhase("idle");
            setExtractError(message);
          }
        }
      }, 2000);
    },
    [stopPolling]
  );

  const onExtractTerminal = useCallback(
    (j: AmbJobPublic, opts: { typedAddress?: string; skipMismatchCheck?: boolean } = {}) => {
      setPhase("idle");
      if (j.status === "review_ready" && j.grid) {
        const typed = (opts.typedAddress ?? addressLine).trim();
        // COMPARE THE STRONGER STRING (2026-08-05). `typed` is the raw shorthand the agent put in
        // the box; `confirmedAddressRef` is what they actually CONFIRMED one screen earlier, which
        // is the provider's normalized, deliverable form whenever the lookup ran. Comparing the
        // typed one against the file meant the gate could fire on two spellings of one address
        // while the app was holding the good spelling the whole time.
        const known = confirmedAddressRef.current || typed;
        const fromFile = typeof j.grid.subject?.address === "string" ? (j.grid.subject.address as string) : null;
        const sourced = j.grid.subject_sourced !== false;
        // GATE-004: the server already knows when the typed subject isn't the file's subject. Ask,
        // never resolve. Two shapes of the same conflict: the file names a DIFFERENT property, or
        // it names no subject at all (`subject_sourced: false` — the "don't borrow a comp" guard).
        // DON'T ASK A QUESTION THAT HAS ONLY ONE ANSWER (2026-08-04, owner).
        //
        // Reported after the address-first step 1 landed: "it pulled data from cotality, and we
        // uploaded the comp file, clicked next and received this window, when the previous window
        // already showed cotality data."
        //
        // This prompt exists to resolve a CONFLICT — the file names one property, the agent typed
        // another, and only a human should decide which the report is about. That is worth
        // interrupting for. But it was also firing on the ordinary, expected case: a comps-only
        // export, which names no subject at all (`subject_sourced: false`). There is no conflict
        // there and nothing to choose between — the only candidate subject is the one already on
        // screen, confirmed a step earlier from Cotality or typed by hand. Asking "which one is
        // the subject?" over a single candidate reads as though the app has forgotten what the
        // agent just told it.
        //
        // So the prompt fires on a genuine disagreement, or when there is genuinely nothing else to
        // go on. A comps-only file plus an already-confirmed subject proceeds, and says what
        // happened in the reading notes rather than in a modal.
        const fileNamesADifferentProperty = sourced && !addressesAgree(known, fromFile);
        const haveSubjectAlready = confirmedSubjectRef.current;
        // THE MISMATCH CHECK IS BACK (2026-08-05, review finding — reverting one part of that
        // morning's change, keeping the rest).
        //
        // What was here for a few hours: `answeredAlready` (a confirmed subject plus a confirmed
        // address) also suppressed the SOURCED branch, so a file naming a DIFFERENT property was
        // reconciled without asking, on the argument that the bypass was disclosed instead. It was
        // not. The note it wrote went into `gaps`, and `applyExtract` — called on the next line —
        // replaced `gaps` wholesale in the same batch; the on-screen half rendered only inside
        // `{step1Grid && …}`. On a restored draft the whole thing was silent.
        //
        // The owner asked for one thing to stop happening: being made to CONFIRM THE SUBJECT twice.
        // That ask is met by the escape below — the comps-only export, which names no subject and
        // therefore poses no question — and by comparing against the address they actually
        // confirmed rather than the shorthand they typed. Neither of those resolves a conflict.
        //
        // A file that positively names a different property IS a conflict, it is the exact case
        // GATE-004 was filed for, and a wrong answer to it silently writes a report about the wrong
        // home. It costs one modal in one situation. It is asked.
        const disagrees =
          known !== "" &&
          (fileNamesADifferentProperty || (!sourced && !haveSubjectAlready));
        if (!opts.skipMismatchCheck && disagrees) {
          setMismatch({ typed: known, fromFile: sourced ? fromFile : null });
          return;
        }
        if (known !== "" && !sourced && haveSubjectAlready) {
          // NOT `setGaps` (2026-08-05, review finding). `applyExtract` below replaces `gaps` with
          // the server's list, in the same batch, so this note used to be written and destroyed
          // without ever rendering — the one disclosure that stands in for the question this branch
          // decides not to ask. `reconcileNotes` is not touched by `applyExtract`.
          addReconcileNote(
            `Your file contains comparable sales only — it names no subject property. The ` +
              `subject's details are the ones you confirmed for ${known}; nothing was borrowed ` +
              `from a comparable.`
          );
        }
        applyExtract(j);
      } else if (j.status === "failed") {
        setExtractError(
          j.reason ??
            "We couldn't read that file. If it's a scan, the text may not be recognizable — try the MLS export or the county sheet instead."
        );
      } else if (j.status === "needs_input") {
        setExtractError(
          j.reason ??
            "We read part of the file but need one more thing before the review can open. Add the subject address above and choose the file again — or send it to hello@valubrief.ai and we'll look at it."
        );
      }
    },
    // `applyExtract` is declared below and is stable for the life of the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addressLine, addReconcileNote]
  );

  function applyExtract(j: AmbJobPublic, subjectOverride?: Record<string, unknown>) {
    if (!j.grid) return;
    const subject = subjectOverride ?? j.grid.subject;
    // THE CAD IS NAMED ON THE ROW (2026-09-25). Only for the file's OWN subject: a subject the agent
    // chose over the file (`subjectOverride`) is not the sheet's and keeps the plain tag.
    const tag = subjectOverride ? UPLOADED_FILE_TAG : extractTag(j.grid);
    if (!subjectOverride && j.grid.cad_identity) setCadIdentity(j.grid.cad_identity);
    if (!subjectOverride && j.grid.subject_photo) {
      setExtractPhoto({ photo: j.grid.subject_photo, source: j.grid.subject_photo_source ?? null });
    }
    // MERGE, NEVER CLOBBER (2026-08-04). This was `setGrid(gridFromExtractedSubject(subject))` — a
    // wholesale replacement, which was harmless while step 1 was only a file, and destructive the
    // moment step 1 became an address lookup the agent reviews. `mergeGrid` fills only fields that
    // are still UNCHECKED, so the file can supply everything the public record didn't and can
    // never overwrite a value a human has confirmed. An empty starting grid makes the two
    // behaviours identical, which is why the file-only path is unchanged.
    setGrid((g) =>
      Object.keys(g).some((k) => g[k]?.checked)
        ? mergeGrid(g, subject, tag)
        : gridFromExtractedSubject(subject, tag));
    // The condition ladder is a human control and is NOT reset by an extract that landed after the
    // agent set it. Only an untouched default gives way to what the file said.
    setCondition((c) =>
      c && c !== "average"
        ? c
        : (typeof subject.condition === "string" ? (subject.condition as string) : "average"));

    // Split what the agent uploaded into the three things it actually contains. The server already
    // separates them; the filter here is belt-and-braces so a competing listing can never reach the
    // comp table even from an older job payload that predates the split.
    const supplied = j.grid.comps ?? [];
    // `isClosedSale`, not `!isCompetingListing`. The negation counted every status that matched
    // neither set — Active Contingent, Active Kick Out, Coming Soon, Withdrawn, Expired — as a
    // completed sale, and dropped it into the priced comp table.
    // An off-market row (withdrawn/expired/cancelled) is in neither table by design — it did not
    // sell, and it is not competition. `amb_report` names each one in `grid.gaps`, which this
    // screen renders. But a job EXTRACTED BEFORE this shipped has off-market rows sitting in
    // `grid.comps` and a `gaps` list computed under the old two-state rules, so the server's
    // disclosure is not there to render. Recompute it here for those rows: kept out of the
    // valuation is right, disappearing without a word is not.
    const closed = supplied.filter(isClosedSale);
    const competing = [...(j.grid.actives ?? []), ...supplied.filter(isCompetingListing)];
    const offMarket = supplied.filter((c) => !isClosedSale(c) && !isCompetingListing(c));

    setComps(
      closed.map((c) => ({
        ...c,
        // START from the server's own recommendation (the top-3 by its rich review ranking) rather
        // than checking everything. Checking everything is how an eight-comp upload silently priced
        // off all eight, and how an unreviewed provider candidate got into a valuation.
        //
        // DEFAULT OFF, NOT ON (2026-08-04). This read `c.selected !== undefined ? !!c.selected :
        // true` — so a payload that omitted `selected` (an older saved job, or any path that
        // doesn't run the ranker) ticked EVERY closed comp. That is precisely the eight-comp
        // failure the comment above warns about, reached through the fallback rather than through
        // the main branch. An absent recommendation is not a recommendation to use everything;
        // false is the only safe reading of silence.
        _selected: c._source === "cotality" ? false : !!c.selected,
        // SEED THE LADDER EXPLICITLY (2026-08-13). A Cotality comp arrives with no `condition`
        // (the provider maps none — condition is a judgment, not a record), and an absent value
        // used to render as the ladder's TOP rung in the dropdown while the engine priced it as
        // "average". Writing the default in at ingestion means the saved draft, the screen and
        // `engine.amb`'s `.get("condition", "average")` all hold the same word. The agent can still
        // change it; what they can no longer do is approve a rung nobody chose.
        condition: normalizeCondition(c.condition),
      }))
    );
    // WHICH COMPETITION GOES IN THE REPORT (2026-08-04). The server hands these back already
    // ranked best-first (`_competing_ranked`), so "the closest matches" is simply the head of the
    // list. `TARGET_COMPS` of them arrive ticked and the rest are one click away — an export with
    // nine actives should not print all nine at a seller presentation, and the agent should not
    // have to tick nine boxes to get the three they want either.
    //
    // This is display only. `engine.amb.select_best` filters to closed sales and the server
    // re-checks every submitted active, so no state of these checkboxes can move a number.
    const _activeTarget = j.grid.target_comps ?? 3;
    setListings(competing.map((c, i) => ({ ...c, _selected: i < _activeTarget })));
    setPriorSales(j.grid.subject_prior_sales ?? []);
    const serverGaps = j.grid.gaps ?? [];
    const offMarketNote =
      offMarket.length && !serverGaps.some((g) => g.includes("without selling"))
        ? [
            `${offMarket.length} of the properties you uploaded came off the market without ` +
              `selling (${offMarket.map((c) => c.name || "an unnamed property").join(", ")}). ` +
              `They are neither sales nor current competition, so they are not used and are not ` +
              `shown in either table. Nothing was estimated from them.`,
          ]
        : [];
    // THIS LINE OWNS THE SERVER'S NOTES AND NOTHING ELSE (2026-08-05, review finding). It is a
    // wholesale replacement on purpose — a second extract's gaps supersede the first's — which is
    // precisely why nothing the CLIENT has told the agent may be stored in here. Those live in
    // `reconcileNotes`, which this cannot reach; `ReadingNotes` renders the two together.
    setGaps([...serverGaps, ...offMarketNote]);
    setFileStatus(j.grid.files ?? []);
    setMinComps(j.grid.min_comps);
    // WHICH HOME THESE SALES WERE READ FOR, recorded at the only moment it is knowable without
    // guessing (2026-08-07). Taken from the subject this extract is being applied WITH, so all
    // three arrivals agree: the file's own subject on the ordinary path, the agent's address on the
    // "use what I typed" answer to GATE-004 (`subjectOverride`), and the already-confirmed subject
    // for a comps-only export that names none. Falling back to `addressLine` last covers the
    // pre-lookup case where the typed line is all there is.
    setCompsAnchor(
      String(subject.address ?? "").trim() || confirmedAddressRef.current || addressLine.trim() || null
    );
    setMismatch(null);
    // The extract lands on step 2, not on a single all-of-it screen. Step 1 was the file and the
    // address, and it is behind them now.
    setStep("detail");

    /* AND THEN THE CASCADE FILLS WHAT THE FILE LEFT BLANK (2026-08-11, spec §2: "gap-filling runs
       across rungs, not instead of them").
       Handing the extract's own output straight back in as the `files` rung is the documented way
       to do this (`cascade_resolve`'s request contract) and it is what makes an uploaded MLS row
       able to carry a public-record lot size. The SERVER decides whether that costs anything: it
       skips the subject lookup when the subject is already complete and skips the comparables call
       when the rungs above it already produced a full set, so a good file spends nothing.
       SILENT, on purpose. With a file in hand the agent already has a working review; a gap-fill
       that could not run has taken nothing away from them, and a red banner over a screen that is
       fine would be the product complaining about itself. The address-only path is the opposite —
       there the cascade IS the action — and it reports its failures loudly. */
    const anchor = String(subject.address ?? "").trim() || confirmedAddressRef.current || addressLine.trim();
    void runCascade(
      {
        address: anchor,
        agent_subject: agentSubjectFromGrid(grid),
        // Only what the file actually SOURCED. `amb_report` already blanks an unsourced subject
        // down to its address, but depending on another module's promise while feeding a rung is
        // how a borrowed comp's GLA becomes the subject's.
        ...(j.grid.subject_sourced === false ? {} : { file_subject: subject }),
        file_comps: closed as unknown as Record<string, unknown>[],
      },
      { advance: false, silent: true }
    );
  }

  /**
   * APPLY ONE RESOLVE. Additive in every direction — it fills, it never replaces.
   *
   *   the subject   merged into the grid, so a field the agent has already ticked is untouchable
   *                 and every row that IS filled carries the tag of the rung that actually produced
   *                 it (`_field_source_labels`), not a single tag for the whole payload.
   *   the comps     APPENDED, and never ticked. Every row is a candidate — the server says so in
   *                 `requires_review` and refuses to generate from an unreviewed provider one — so
   *                 nothing here can put a sale into a valuation. Rows the screen already holds are
   *                 matched by the same punctuation-blind address rule the added-file merge uses,
   *                 so a second pass cannot double a comp.
   *   the reporting kept whole in `cascade`, which is what the ladder, the outage panel, the gap
   *                 list and the per-cell tags all read.
   */
  function applyCascade(result: CascadeResolveResult, args: CascadeResolveArgs, advance: boolean) {
    setCascade(cascadeMeta(result));
    setHeldBack(heldRowsFromCascade(result.held_back).filter(isClosedSale));
    // A CAD-LOCKED SUBJECT IS NEVER FILLED FROM COTALITY (provenance veto, 2026-10-04). The server
    // does not ask Cotality once it has the lock; this is the second wall, so no answer — from any
    // server — can put a public-aggregate figure beside a subject the agent locked to a county
    // record. What the record does not state stays a gap the agent fills.
    const subject = args.cad_pick ? subjectWithoutProviderFields(result.subject) : result.subject;
    setGrid((g) =>
      Object.keys(g).some((k) => g[k]?.checked)
        ? mergeCascadeSubject(g, subject)
        : gridFromCascadeSubject(subject));
    // `isClosedSale` for the same reason `applyExtract` uses it: this table prices reports, and a
    // row that is not a completed sale has no business in it. The cascade's comparables rung
    // returns sales, so this normally removes nothing — it is the second of two cheap guards.
    const incoming = compsFromCascade(result.comps).filter(isClosedSale);
    setComps((cs) => {
      const seen = new Set(cs.map((c) => normalizeAddress(c.name)));
      return [...cs, ...incoming.filter((c) => !seen.has(normalizeAddress(c.name)))];
    });
    // NTREIS MLS COMPETITION (Release 1.1). Only when the server sent it (the MLS rung ran for this
    // user): appended to the competing listings, never to the comps, none ticked by the client
    // beyond the server's best-first head — the same rule the file path uses.
    if (result.mls_actives && result.mls_actives.length) {
      const mlsActives = compsFromCascade(result.mls_actives);
      setListings((ls) => {
        const seen = new Set(ls.map((c) => normalizeAddress(c.name)));
        const fresh = mlsActives.filter((c) => !seen.has(normalizeAddress(c.name)));
        return [...ls, ...fresh.map((c, i) => ({ ...c, _selected: ls.length + i < 3 }))];
      });
    }
    setCompsAnchor((a) =>
      a || String(result.subject.address ?? "").trim() || result.address || null);
    if (advance) setStep("detail");
  }

  /**
   * Ask the cascade once, and keep the arguments so the outage panel's "Try again" can ask the same
   * question again rather than a rebuilt one.
   *
   * `advance` moves the flow onto step 2 — true only for the address-first entry, where this call
   * IS the thing the agent pressed. A retry from step 3, and the gap-fill pass after an upload,
   * both leave the agent where they are.
   */
  async function runCascade(requested: CascadeResolveArgs, opts: { advance: boolean; silent?: boolean }) {
    // EVERY CASCADE CALL CARRIES THE CURRENT PICK'S LOCK, or none when there is no pick — the
    // address-only path, the gap-fill after a file read, and the outage retry alike.
    const args = cascadeArgsWithPick(requested, cadPickRef.current?.lock);
    lastResolveArgs.current = args;
    setResolving(true);
    setCascadeError(null);
    try {
      applyCascade(await resolveCascade(args), args, opts.advance);
    } catch (e) {
      // §6: a 429 limit_reached means the request was refused, not that the search failed — so
      // the outage copy below (which invites "try again") would be the wrong story. Still
      // refreshes the shared usage cache even when `silent`, so a background retry does not keep
      // re-triggering the same refusal against a count that is already known to be exhausted.
      if (isLimitReached(e)) {
        refreshUsage();
        if (!opts.silent) setLimitRefusal(e.body.message);
        return;
      }
      if (opts.silent) return;
      // NOT "no comparable sales were found" — see spec §6 and `cascade.ts`'s rule 1. A request
      // that never completed has ruled nothing out, and saying otherwise sends an agent hunting
      // for a property that was always there.
      const why = e instanceof Error && e.message ? ` — ${e.message}` : "";
      setCascadeError(
        `We couldn't reach the public-records search${why}. Nothing has been ruled out — this is ` +
        `not the same as finding no sales. Try again, or add your comps export above; neither the ` +
        `subject nor the report depends on this working.`
      );
    } finally {
      setResolving(false);
    }
  }

  /** The retry behind the outage panel. Re-asks the last question when there is one, and otherwise
   * asks the plain address-only one, so the button is never a no-op. */
  function retryCascade() {
    const args = lastResolveArgs.current
      ?? { address: subjectAnchor, agent_subject: agentSubjectFromGrid(grid) };
    void runCascade(args, { advance: false });
  }

  // THE THIRTY DAYS IS A CEILING, NOT A PROMISE FOR BRIEFS THAT GET REOPENED (2026-09-07).
  // `loadDraft` expires the draft it is asked for, but the draft nobody ever reopens is never
  // asked for — and that is exactly the one that should not still be on the machine. Once on
  // mount, cheap, and never fatal.
  useEffect(() => { sweepExpiredDrafts(); }, []);

  // UI-009 — rehydrate from `/cma/:jobId`. A reload used to land on an empty intake form with no
  // hint that a 34-second job of theirs was still running server-side.
  useEffect(() => {
    if (!jobId || rehydratedFor.current === jobId) return;
    rehydratedFor.current = jobId;
    let cancelled = false;
    let settled = false;
    getJob(jobId)
      .then(async (j) => {
        if (cancelled) return;
        settled = true;
        setJob(j);
        // THE THIRD PLACE A DRAFT CAN BE, AND THE ORDER IS NEAREST FIRST (2026-09-07).
        //
        // `loadDraft` reads this tab, then this machine. Both are local, both are synchronous, and
        // both come back empty on a machine that never ran the wizard — which is the whole of what
        // the owner reported as "we can no longer go back to any stage or step". The server copy is
        // asked for ONLY when they do, so an agent still working in this tab never waits on a
        // request to see their own grid, and a save that has not landed yet cannot be overwritten
        // by the one before it.
        const draft = loadDraft<PersistedDraft>(jobId)
          ?? await loadServerDraft<PersistedDraft>(jobId);
        // Re-checked after the await: an agent who navigated away mid-fetch must not have a brief
        // they have left rehydrated over the one they went to.
        if (cancelled) return;
        if (draft) {
          setGrid(draft.grid ?? {});
          setComps(draft.comps ?? []);
          setListings(draft.listings ?? []);
          setPriorSales(draft.priorSales ?? []);
          setGaps(draft.gaps ?? []);
          setReconcileNotes(draft.reconcileNotes ?? []);
          setSubjectNotice(draft.subjectNotice ?? null);
          setCondition(draft.condition ?? "average");
          setAddressLine(draft.addressLine ?? "");
          setStep1Grid(draft.step1Grid ?? null);
          if (draft.tier) setTier(draft.tier);
          if (draft.personas?.length) setPersonas(draft.personas);
          else if (draft.persona) setPersonas([draft.persona]);
          if (draft.valueAdjustments) setValueAdjustments(draft.valueAdjustments);
          if (draft.marketConditions) setMarketConditions(draft.marketConditions);
          if (typeof draft.extraContext === "string") setExtraContext(draft.extraContext);
          if (typeof draft.preparedFor === "string") setPreparedFor(draft.preparedFor);
          // THE THREE THINGS A SECOND BUILD NEEDS (2026-08-07). All absent on a draft written
          // before this shipped, and each absence reads as the old behaviour: no siblings, no
          // remembered extract, no re-voice offered.
          if (draft.personaJobs?.length) setPersonaJobs(draft.personaJobs);
          // Voices already opened into their own tab. Restored so a reload of THIS tab does not
          // re-offer one the agent has already spent a model call on.
          if (draft.revoicedAway?.length) setRevoicedAway(draft.revoicedAway);
          if (draft.extractJobId) setExtractJobId(draft.extractJobId);
          if (draft.confirmedPayload) setConfirmedPayload(draft.confirmedPayload);
          else if (draft.confirmedPayloadWithheld) setPayloadWithheld(true);
          // Restored WITH the comps it describes, in the same batch. A draft that brought back a
          // comparable set without the address it was read for would leave the flow unable to
          // notice a subject that had since moved — which is the one thing this anchor exists for.
          if (draft.compsAnchor) setCompsAnchor(draft.compsAnchor);
          // Restored WITH everything else the agent was told. An outage that vanished on reload
          // would leave a short comp set looking like a clean, complete answer.
          if (draft.cascade) setCascade(draft.cascade);
          // The locked county record and its change ledger. A ledger lost to a reload would send
          // Generate a subject with no history of the switch the agent made.
          if (draft.subjectCadPick) setSubjectCadPick(draft.subjectCadPick);
          if (draft.subjectChangeLog?.length) setSubjectChangeLog(draft.subjectChangeLog);
        }
        // THE EXTRACT ID WHEN THE DRAFT DIDN'T CARRY ONE. A job the URL points at is the extract
        // itself whenever it is still reviewable or still reading — that is what its status and
        // stage vocabulary mean. A BUILD job is never assumed to be one: guessing wrong here is the
        // exact way `sourceJobId` starts naming a report and every verified photo gets stripped.
        if (!draft?.extractJobId && (j.status === "review_ready" || looksLikeExtractStage(j.stage))) {
          setExtractJobId(j.id);
        }
        if (j.status === "review_ready") {
          // A saved draft is the user's own reviewed state and outranks a fresh read of the
          // server's extract — restoring the server copy over it would silently discard their edits.
          // The step comes back with it: a draft written before `step` was persisted has none, and
          // falls back to the head of the reviewing sequence rather than guessing.
          if (draft?.grid && Object.keys(draft.grid).length) {
            setStep(isReviewStep(draft.step) ? draft.step : "detail");
          }
          // No saved review yet — this is a job that finished while the tab was away. Run the same
          // terminal handling a live extract gets, GATE-004 included: a refresh must not be a way
          // to skip the question.
          else onExtractTerminal(j, { typedAddress: draft?.addressLine ?? "" });
        } else if (j.status === "done") {
          setStep("build");
        } else if (j.status === "failed" || j.status === "needs_input") {
          setStep("build");
        } else if (looksLikeExtractStage(j.stage)) {
          setPhase("extracting");
          setStep("intake");
          // Thread the restored address explicitly: `onExtractTerminal` closes over the `addressLine`
          // of the render that created it, which is still "" at this point.
          pollJob(j.id, (jj) => onExtractTerminal(jj, { typedAddress: draft?.addressLine ?? "" }), EXTRACT_TERMINAL);
        } else {
          setStep("build");
          pollJob(j.id, () => {}, BUILD_TERMINAL, setSubmitError);
        }
      })
      .catch(() => {
        if (cancelled) return;
        settled = true;
        // An unknown/expired job id is not an error state worth a red banner — drop the user on a
        // clean intake with the URL cleaned up, rather than a screen about a job they can't reach.
        navigate("/cma", { replace: true });
      });
    return () => {
      cancelled = true;
      // React 18 StrictMode mounts, unmounts and re-mounts every effect in development. Leaving the
      // guard set after a cancelled-before-it-resolved run made the SECOND pass return early, so
      // rehydration silently did nothing in dev — the exact defect UI-009 exists to fix.
      if (!settled) rehydratedFor.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // `localReadOnly`, not `forceFastPdf` (2026-08-07). The intake checkbox was INVERTED when the
  // fast read became the default: it used to ask the agent to opt IN to the converter, and it now
  // asks them to opt OUT and keep the file on our own servers. This destructure was still reading
  // the retired name, so the new checkbox typechecked (a structural subset satisfies the param
  // type) and was a NO-OP end to end — an agent could tick "read this file on our own servers" and
  // the document went out to the converter anyway. `forceFastPdf` is still accepted by `api.ts` for
  // a stale cached tab; it is simply no longer rendered, so nothing here passes it.
  async function handleStart({ files, addressLine: addr, subjectPhoto, localReadOnly }:
                             { files: File[]; addressLine: string; subjectPhoto?: File | null;
                               localReadOnly?: boolean }) {
    // THE ADDRESS-ONLY ENTRY IS A DIFFERENT WAIT (2026-08-11). There is no upload and no file to
    // read, so `phase` stays idle and the extract progress panel — four stages about reading a
    // document — never mounts. `resolving` drives the one line that IS true: we are looking the
    // address up.
    // Idle until we know the file is worth reading: the first thing an upload now does is ask the
    // free sources, and that wait is the `resolving` line, not the four-stage extract panel.
    setPhase("idle");
    setSkippedUpload(null);
    setExtractError(null);
    setCascadeError(null);
    setStalled(false);
    // Both halves of the reconciliation disclosure are about the file being replaced right now, so
    // they go with it. Whatever this upload turns out to be, it gets to say its own piece.
    setSubjectNotice(null);
    setReconcileNotes([]);
    setAddressLine(addr);
    // WHAT THE AGENT ALREADY CONFIRMED ON STEP 1 SURVIVES THE EXTRACT (2026-08-04). They looked the
    // address up, read what came back, ticked and corrected it. Throwing that away and rebuilding
    // the grid from the file would make them do the same work twice — and would silently replace a
    // figure they had just checked with one read off a sheet. `applyExtract` merges into this
    // instead; see the note there.
    //
    // THE GRID IS NO LONGER COPIED ACROSS AT THIS POINT (2026-08-05): step 1 edits this component's
    // `grid` directly, so there is one grid and nothing to copy. What is recorded here instead is
    // the LINE — the subject exactly as they left step 1 — so step 2 can present what the file
    // added on top of it rather than the whole form over again.
    setStep1Grid(Object.keys(grid).length ? grid : null);
    // Recorded by `readFiles`, not here (2026-09-22): a file the resolver made unnecessary was never
    // read, and "Your review of the last file" / "Back to your file" would both be false about it.
    setFileNames([]);
    // A NEW FILE INVALIDATES THE LAST CONFIRMED PAYLOAD (2026-08-07). "Rebuild in another voice"
    // exists to re-send the payload the agent confirmed FOR THIS PROPERTY, unchanged. Carrying one
    // across a fresh upload would let a button on the next report rebuild the previous property.
    setConfirmedPayload(null);
    setPayloadWithheld(false);
    setPersonaJobs([]);
    setRevoiceError(null);

    /* NO FILE IS THE ORDINARY PATH, NOT A DEGRADED ONE (2026-08-11, spec §2's "soft pass").
       An agent without MLS membership cannot produce a comps export at all, and this product is
       for them. The cascade is asked directly: it resolves the subject from the public record and
       the comparables from the same place, both in the engine shape the review grid already reads,
       and lands on step 2 exactly as an upload does. No warning, no "reduced accuracy" note, no
       second-class framing — the spec is explicit that the soft pass is silent. */
    if (!files.length) {
      await runCascade(
        { address: addr, agent_subject: agentSubjectFromGrid(grid) },
        { advance: true }
      );
      return;
    }

    /* RESOLVE FIRST, READ ONLY IF IT IS WORTH READING (2026-09-22, owner direction; spec §2.1).
       Reading a file is one model call over the whole document, so the saving is binary. Ask
       MLS -> Cotality -> the county record with NO file input and `upload_pending`, and read the
       file only when the resolver says gaps remain (`file_parse.parse`). When nothing is missing,
       the file is NOT read, the grid lands from the public record, and `UploadSkippedPanel` says so
       and offers "Use my file instead" — the agent's data wins the moment they ask for it.

       EVERY OTHER OUTCOME READS THE FILE, EXACTLY AS BEFORE: gaps remain, the resolve failed or
       timed out (a request that never completed has ruled nothing out), an older server that sends
       no `file_parse`, or a subject photo attached — the photo travels only with the extract, and
       skipping the extract would drop it without a word. */
    if (!subjectPhoto) {
      const args: CascadeResolveArgs = cascadeArgsWithPick({
        address: addr, agent_subject: agentSubjectFromGrid(grid), upload_pending: true,
      }, cadPickRef.current?.lock);
      setResolving(true);
      try {
        const r = await resolveCascade(args);
        if (r.file_parse?.has_upload === true && r.file_parse.parse === false) {
          lastResolveArgs.current = args;
          applyCascade(r, args, true);
          setSkippedUpload({ files, addressLine: addr, localReadOnly });
          return;
        }
      } catch {
        // absence-ok: the pre-check is an optimisation. Failing it costs only the model call it
        // would have saved; the file is read below exactly as it was before this existed.
      } finally {
        setResolving(false);
      }
    }
    await readFiles({ files, addressLine: addr, subjectPhoto, localReadOnly });
  }

  /** The extract, as it always ran. Called by `handleStart` when the file is worth reading, and by
   * "Use my file instead" when the agent overrides a skip. */
  async function readFiles({ files, addressLine: addr, subjectPhoto, localReadOnly }:
                           { files: File[]; addressLine: string; subjectPhoto?: File | null;
                             localReadOnly?: boolean }) {
    setPhase("uploading");
    setSkippedUpload(null);
    setFileNames(files.map((f) => f.name));
    setExtractError(null);
    try {
      const j = await startExtract({ files, tier, addressLine: addr, subjectPhoto, localReadOnly });
      setJob(j);
      // The extract job's id, recorded where it cannot be overwritten by the build job later — see
      // `extractJobId`. This is the only place a first-hand extract id is created.
      setExtractJobId(j.id);
      setPhase("extracting");
      // UI-009: the job id belongs in the URL from the moment it exists, so a refresh mid-extract
      // has something to come back to.
      navigate(`/cma/${j.id}`, { replace: true });
      rehydratedFor.current = j.id;
      if (EXTRACT_TERMINAL.includes(j.status)) {
        onExtractTerminal(j, { typedAddress: addr });
      } else {
        pollJob(j.id, (jj) => onExtractTerminal(jj, { typedAddress: addr }), EXTRACT_TERMINAL);
      }
    } catch (e) {
      setPhase("idle");
      // §6: a refused extract is not a file-reading failure.
      if (isLimitReached(e)) {
        setLimitRefusal(e.body.message);
        refreshUsage();
        return;
      }
      setExtractError(
        "We couldn't start reading your file. Nothing was uploaded, so nothing is half-done — check the file opens on your machine, then choose it again."
      );
    }
    // UI-050: busy/phase is intentionally NOT cleared in a finally here. It must stay live across
    // the whole extract — through the ~30s of polling — until onExtractTerminal fires. Clearing it
    // at job creation (~1s in) re-enabled the button on an inert screen and invited duplicate jobs.
  }

  function handleStartOver() {
    stopPolling();
    stopMerge();
    // Not `clearDraft(job.id)` — see `clearAllDrafts`. One pass through the wizard leaves a draft
    // under the extract job's key AND the build job's, and the homeowner's name is in both.
    clearAllDrafts();
    setPhase("idle");
    setJob(null);
    // START OVER MEANS START OVER, AND THAT NOW INCLUDES THE SECOND-BUILD STATE (2026-08-07). The
    // extract id, the confirmed payload and the sibling voices all belong to the job that is
    // ending. Left standing, the next property's first submit would quote the previous property's
    // extract as its photo authority and its report screen would offer to rebuild a payload for a
    // house the agent is no longer looking at.
    setExtractJobId(null);
    setConfirmedPayload(null);
    setPayloadWithheld(false);
    setPersonaJobs([]);
    setRevoicing(null);
    setRevoiceError(null);
    setGrid({});
    setComps([]);
    setListings([]);
    setPriorSales([]);
    // Cleared with the comps it describes, always. An anchor left standing over an empty comp set
    // would make the next property's first extract look like a subject that had moved.
    setCompsAnchor(null);
    setPendingStep(null);
    setGaps([]);
    setReconcileNotes([]);
    setMismatch(null);
    setFileNames([]);
    setSkippedUpload(null);
    setExtractError(null);
    setReviewError(null);
    setSubmitError(null);
    setNotice(null);
    setInfo(null);
    setStalled(false);
    // The last resolve belongs to the property that is ending: its ladder, its outage, its gap
    // list and the address it was asked about. Left standing, the next property's first screen
    // would carry the previous one's "we couldn't ask" panel and a Try again that re-asks about a
    // home the agent has left. `cascadeStatus` is NOT cleared — the gates are a fact about this
    // deployment, not about this job.
    setCascade(null);
    setCascadeError(null);
    setResolving(false);
    lastResolveArgs.current = null;
    setValueAdjustments({});
    setExtraContext("");
    setPreparedFor("");
    setCadIdentity(null);
    setConfirmedSubjectPhoto(null);
    setPhotoChoiceKey(null);
    setUseOwnerOfRecord(false);
    setExtractPhoto(null);
    // START OVER MEANS START OVER (2026-08-05). These five were left standing: the address box came
    // back holding the previous property's address behind a now-empty grid, the condition ladder
    // kept whatever the last job had set it to, the previous file and photo were still attached,
    // and step 2 would have described the fresh subject as a diff against a discarded one. A blank
    // form holding the last job's address is worse than either a blank form or a kept one, because
    // it looks like the first and behaves like the second.
    setAddressLine("");
    setSubjectClip(null);
    setSubjectCadPick(null);
    setSubjectChangeLog([]);
    setCondition("average");
    setIntake(EMPTY_INTAKE);
    setStep1Grid(null);
    setSubjectNotice(null);
    setFileStatus([]);
    setStep("intake");
    navigate("/cma", { replace: true });
  }

  /**
   * Session 8 (board doc §5: "confirm the fetch actually falls through to Cotality, not just
   * Collin CAD"): the county CAD lookup only covers whatever's in `data/` today (Collin only, per
   * `lane4-infra.md`'s Session 7 notes) — the server's `/api/subject` has no Cotality fallback of
   * its own (confirmed by reading it: a no-match just returns guidance, nothing else is tried). So
   * this fallback is CLIENT-orchestrated, not a claim that the server does it: county CAD first
   * (the more authoritative record when it's loaded), and only on an honest no-match does this
   * automatically try the existing, already-approved Cotality subject-fill path
   * (`cotalityLookupSubject`, the same one the file-upload/lookup flow already uses for internal
   * subject-fill — no new legal exposure, Cotality data was already an approved subject-fill
   * source before this session). Tags the result "Cotality public records" honestly, never as if
   * it were the county record.
   */
  async function handleFetchPublicRecords() {
    // THE OWNER'S RULE IS ABSOLUTE (2026-10-04): "If the agent picked a CAD row, we never call
    // Cotality at all." The control is not rendered for a locked subject (`lockedRecordNote`); this
    // guard is the second wall, so no other caller can reach Cotality for a CAD-locked subject.
    if (subjectCadPick) return;
    // COTALITY FIRST, ALWAYS (2026-08-01, owner directive).
    //
    // This used to search a loaded county CAD dataset and only reach Cotality if that returned a
    // clean empty. Two things made that unusable in practice:
    //
    //   1. ONE county is loaded in production — Collin (`/api/next/counties/loaded`). The picker
    //      defaulted to `counties[0]`, which is not a default so much as the only option, and the
    //      subject's own ZIP was never consulted. The owner's test property, 4815 Springtree Lane
    //      in Sachse, is on the DALLAS side of a city that straddles two counties, so the search
    //      could not succeed for it at any point, ever.
    //   2. An unknown address makes `/api/subject` THROW rather than resolve empty, so control
    //      jumped to the outer catch and the Cotality fallback was never attempted at all. The
    //      agent got "the public-records search didn't come back" while the provider that covers
    //      the entire country — and that resolves this exact address in its type-ahead — sat
    //      untried.
    //
    // Cotality is national and is already an approved subject-fill source. It is now the path, not
    // the fallback. The county dataset is only consulted if Cotality finds nothing, and its
    // failure can no longer suppress anything.
    const q = subjectAnchor.trim();
    if (!q) {
      setReviewError(
        "Enter the subject's address first — the public-records search needs an address to look up."
      );
      return;
    }
    // The clip step 1 resolved, but only while it still belongs to the address on screen. Compared
    // on a normalised form because the provider returns its own casing and punctuation for an
    // address the agent typed — "4815 Springtree Ln." and "4815 SPRINGTREE LN" are the same
    // property, and treating them as different would throw the saving away for nothing.
    const reusableClip =
      subjectClip && sameAddress(subjectClip.address, q) ? subjectClip.clip : null;
    setFetchingPublicRecords(true);
    setReviewError(null);
    setInfo(null);
    try {
      let cotalityFailure: string | null = null;
      let offline: CotalityOfflineBody | null = null;
      // True when the public-records lookup could not run at all (an outage), as opposed to running
      // and finding nothing. An outage blocks nothing, so every message about it is calm (status,
      // not alert) and vendor-neutral (2026-10-17).
      let unavailable = false;
      // DORMANT (owner, 2026-10-20): the public-records connector is parked, so it is not asked
      // at all — not asked and refused — and nothing on this screen mentions it. The county
      // record below is asked exactly as it is when a lookup finds nothing.
      const dormant = publicRecordsDormant(cascadeStatus?.gates);
      if (!dormant) try {
        const cot = await cotalityLookupSubject(
          reusableClip ? { clip: reusableClip } : { address: q }
        );
        // NOT `Object.keys(cot.data).length > 0` — `project_subject` ALWAYS returns `_source`,
        // `confirmed`, `pool`, `garage` and `construction`, so that test is true even when every
        // mapped field came back null. It reported success on a fill that changed nothing: the
        // grid stayed empty, the blocker stayed red, and the status line said it had worked.
        // Count the fields that would actually land instead.
        const filled = Object.entries(cot.data ?? {}).filter(
          ([k, v]) => !k.startsWith("_") && v !== null && v !== undefined && v !== ""
        );
        if (cot.clip) setSubjectClip({ clip: String(cot.clip), address: q });
        if (filled.length > 0) {
          setGrid((g) => mergeGrid(g, cot.data, cot.source_tag || "Cotality public records"));
          setInfo(
            `Filled ${filled.length} field${filled.length === 1 ? "" : "s"} from Cotality public records. Every one is source-tagged — check them before you generate.`
          );
          return;
        }
        cotalityFailure = "The public-records lookup found no property record for that address.";
      } catch (e) {
        // §6: a 429 limit_reached means the request was refused before it ran — falling through
        // to the CAD county lookup below would only earn a SECOND refusal for the same reason, so
        // this returns immediately (the outer `finally` below still clears `fetchingPublicRecords`).
        if (isLimitReached(e)) {
          setLimitRefusal(e.body.message);
          refreshUsage();
          return;
        }
        // Surface WHY rather than swallowing it. The old bare `catch {}` here is why "Cotality
        // isn't delivering anything" was impossible to diagnose from the screen.
        //
        // THE OFFLINE ENVELOPE IS READ, NOT RE-WORDED (slice S05). A provider outage now comes
        // back as HTTP 409 with `{code, cad_required, message, how}` — a body Cloudflare passes
        // through, unlike the 502 that produced a blank screen in production on 2026-09-22. We
        // keep the SERVER's sentences rather than mirroring them here: two copies of one owner-set
        // string is how the two come to disagree.
        //
        // 2026-10-17: the server's sentence is no longer printed. It names the vendor and says "CAD
        // data is needed", and it was shown with a doubled period. `offline` still decides the
        // branch below; the words the agent reads are vendor-neutral and calm.
        offline = offlinePayload(e);
        unavailable = true;
        cotalityFailure = "The public-records lookup isn't available right now.";
      }

      // ---------------------------------------------------------------------------------------
      // THE CAD FALL-THROUGH, AND THE COUNTY IT ASKS ABOUT  (slice S05, 2026-09-22)
      //
      // THE DEFECT. This gated on `selectedCounty`, which is `counties[0] ?? null` — "the first
      // loaded county", which is not a default so much as the only option, and is NEVER the
      // county the address is in. The subject's own ZIP was never consulted. It is the same bug
      // the comment above this function already describes for the OLD ordering, left in place by
      // the 2026-08-01 fix because that change only moved Cotality in front of it.
      //
      // THE FIX. `cadAvailableForAddress` is the ONE predicate (`server/cad_availability.py`):
      // it takes the ZIP out of the address and asks which loaded dataset actually holds records
      // there. It is free — it opens no repository and reads no property row — and it answers
      // about THIS address. When it names a county, we look the subject up in that county.
      //
      // AND AN OUTAGE IS STILL NOT A NO-MATCH. `/api/subject` now returns `status` alongside
      // `guidance`, so "we hold nothing for that county" and "we looked and it is not there" stop
      // arriving as the same empty answer (`registry.subject_result`).
      // ---------------------------------------------------------------------------------------
      let cad: CadAvailability | null = null;
      // Captured so the "NOTHING ANSWERED" section below can tell an AMBIGUOUS match (we looked,
      // more than one property fit) apart from a genuine no-match (we looked, nothing fit) — see
      // `registry.SUBJECT_AMBIGUOUS` / `ingestion/query.py`'s address-ambiguity fix. Both leave
      // `result.subject` null, so `status` is the only signal that tells them apart.
      let countyStatus: string | undefined;
      try {
        cad = await cadAvailableForAddress(q);
      } catch {
        /* The predicate is an optimisation, not a gate. If it cannot answer we simply do not
           have a county to ask, and the honest message below is what the agent gets. */
      }
      if (cad && cad.county && cad.state) {
        const countyName = cad.county;
        try {
          const result = await lookupCountySubject(cad.state, countyName, q);
          countyStatus = result.status;
          if (result.subject) {
            const mapped = countySubjectToGrid(result.subject as Record<string, unknown>);
            const gained = Object.entries(mapped).filter(
              ([k, v]) => k !== "address" && v !== null && v !== undefined && v !== ""
            ).length;
            setGrid((g) => mergeGrid(g, mapped, `${countyName} CAD`));
            // SAY WHAT ACTUALLY LANDED. "Filled from the county record" printed over a grid that
            // gained nothing is the same false success the Cotality branch above was fixed for.
            const flagNote = livingAreaFlagNote(
              (result.subject as Record<string, unknown>).living_area_flagged
            );
            setInfo(
              (gained > 0
                ? `Filled ${gained} field${gained === 1 ? "" : "s"} from the ${countyName} County record. Every one is source-tagged — check them before you generate.`
                : `The ${countyName} County record matched that address but carried none of the fields the report needs. Enter them below.`) +
                (flagNote ? ` ${flagNote}` : "")
            );
            return;
          }
        } catch {
          /* A county miss is expected for anywhere we have no data. It is not the headline. */
        }
      }

      // NOTHING ANSWERED. Say the true thing, in the place the control was pressed.
      //
      // When we hold no CAD for this address either, this is the one condition under which the
      // owner's sentence is true, and it is printed verbatim with the one line that says how to
      // unblock it. When we DO hold the county and it simply had no record, the Cotality failure
      // stays the headline — because the county genuinely answered, and saying "CAD data is
      // needed" about a county record we just read would be false.
      const haveCounty = Boolean(cad && cad.county);
      // An outage is reported on the calm status line, never the alert line.
      const report = (unavailable || dormant) ? setInfo : setReviewError;
      // While dormant there is no lookup result to lead with, so the county's answer stands alone.
      const lead = cotalityFailure ? `${cotalityFailure} ` : "";
      if (!haveCounty && offline && offline.cad_required) {
        // Nothing automatic could answer and we hold no county record for this address.
        setInfo(PUBLIC_RECORDS_UNAVAILABLE);
      } else if (haveCounty && countyStatus === "ambiguous") {
        // MORE THAN ONE PROPERTY MATCHED. This is not "holds no property at that address" — the
        // county record answered, it just answered with more than one candidate (e.g. several
        // Rowlett properties at one street address). Saying "holds no property" here would be
        // false, exactly the claim this fix removes.
        report(
          `${lead}More than one property matches that address in the ${cad?.county} County record. Pick the one you mean from the address list, add the unit number, or enter the subject's details in the grid above.`
        );
      } else {
        report(
          haveCounty
            ? `${lead}The ${cad?.county} County record was searched${dormant ? "" : " too"} and holds no property at that address. Enter the subject's details in the grid above, or upload the county/CAD sheet — neither depends on a lookup.`
            : `${lead}Enter the subject's details in the grid above, or upload the county/CAD sheet for that address — neither depends on a lookup.`
        );
      }
    } finally {
      setFetchingPublicRecords(false);
    }
  }

  async function handleAddFileMerge(files: File[]) {
    mergeAbandoned.current = false;
    setMerging(true);
    setReviewError(null);
    const names = files.map((f) => f.name).join(", ");
    try {
      // `subjectAnchor`, NOT `addressLine` — see its declaration. An added comps-only sheet has no
      // subject of its own, so the server's anchor for "which of these rows is the subject's own
      // prior listing?" can only come from the review the agent is already looking at.
      const j = await startExtract({ files, tier, addressLine: subjectAnchor });
      // Wait for this follow-up job to reach review_ready, then merge (never restart the draft).
      const wait = () =>
        new Promise<AmbJobPublic>((resolve, reject) => {
          if (mergeTimer.current) clearInterval(mergeTimer.current);
          mergeTimer.current = setInterval(async () => {
            if (mergeAbandoned.current) {
              if (mergeTimer.current) clearInterval(mergeTimer.current);
              mergeTimer.current = null;
              reject(new Error("abandoned"));
              return;
            }
            try {
              const jj = await getJob(j.id);
              if (EXTRACT_TERMINAL.includes(jj.status)) {
                if (mergeTimer.current) clearInterval(mergeTimer.current);
                mergeTimer.current = null;
                resolve(jj);
              }
            } catch (e) {
              if (mergeTimer.current) clearInterval(mergeTimer.current);
              mergeTimer.current = null;
              reject(e);
            }
          }, 2000);
        });
      const finished = EXTRACT_TERMINAL.includes(j.status) ? j : await wait();
      // The user may have started over while this was in flight — never write a discarded job's
      // rows into the grid they are looking at now.
      if (mergeAbandoned.current) return;
      if (finished.status === "review_ready" && finished.grid) {
        // Only merge subject facts the added sheet actually SOURCED. `amb_report.py` already blanks
        // an unsourced subject down to its address before sending it, but that is another module's
        // promise about a payload this one is about to write into the agent's confirmed grid —
        // depending on it silently is how a borrowed comp's GLA becomes the subject's.
        if (finished.grid.subject_sourced !== false) {
          setGrid((g) => mergeGrid(g, finished.grid!.subject, extractTag(finished.grid)));
        }
        // PUNCTUATION-BLIND DEDUPE (2026-08-04). This keyed on `.toLowerCase()` and nothing else,
        // so "3611 Cedar Creek Lane Sachse, TX 75048" and "3611 Cedar Creek Lane, Sachse, TX
        // 75048" both survived the filter and the same home appeared twice — once in the grid the
        // agent reviews and, because `amb_build._norm_listing_name` had the identical hole, once
        // again in the delivered report. Both halves now use the same rule: `normalizeAddress`
        // here, its literal Python twin there. Fixing one alone would have left the other lying.
        const existingNames = new Set([...comps, ...listings].map((c) => normalizeAddress(c.name)));
        const incoming = [...(finished.grid.comps ?? []), ...(finished.grid.actives ?? [])]
          .filter((c) => !existingNames.has(normalizeAddress(c.name)));
        // An added file can carry either kind. Route each row to the right table.
        //
        // HONOUR THE SERVER'S FLAG HERE TOO (2026-08-04). This ticked every incoming non-Cotality
        // closed sale unconditionally, ignoring `selected` entirely — so adding a second sheet
        // could quietly put eight more comps into the priced set behind the agent's back, and a
        // row the server had already judged unusable arrived checked. Same rule as the initial
        // seed above: an absent recommendation reads as false.
        setComps((cs) => [...cs, ...incoming.filter(isClosedSale)
          .map((c) => ({ ...c, _selected: c._source === "cotality" ? false : !!c.selected,
                         condition: normalizeCondition(c.condition) }))]);
        setListings((ls) => [...ls, ...incoming.filter(isCompetingListing)]);
        const fg = finished.grid;
        // An added sheet's rows join a set that already has an anchor, so the anchor stands. It is
        // set here only when there wasn't one — a draft written before the anchor existed, whose
        // comps would otherwise stay permanently unwarnable.
        setCompsAnchor((a) => a || subjectAnchor || null);
        if (fg.subject_prior_sales?.length) setPriorSales((ps) => [...ps, ...fg.subject_prior_sales!]);
        if (fg.gaps?.length) setGaps((g) => [...g, ...fg.gaps!]);
      } else {
        setReviewError(
          `We couldn't read ${names}. Your review is untouched, nothing was lost — try a different export of the same data.`
        );
      }
    } catch (e) {
      if (!mergeAbandoned.current) {
        // §6: a refused upload is not a read failure.
        if (isLimitReached(e)) {
          setLimitRefusal(e.body.message);
          refreshUsage();
        } else {
          setReviewError(
            `We couldn't read ${names}. Your review is untouched, nothing was lost — try a different export of the same data.`
          );
        }
      }
    } finally {
      if (mergeTimer.current) {
        clearInterval(mergeTimer.current);
        mergeTimer.current = null;
      }
      setMerging(false);
    }
  }

  /**
   * THE PAYLOAD BUILDER, LIFTED OUT OF `handleSubmit` (2026-08-07, owner request: "the ability to
   * go back to any of the steps in the workflow to regenerate the file with a different persona or
   * make adjustments without rerunning the entire workflow").
   *
   * Nothing inside it changed — it is the same code that has been building the confirm payload,
   * given a name so it can be called from somewhere other than the Generate button. What it returns
   * is stored (`confirmedPayload`) and handed back VERBATIM for every later voice; see
   * `ConfirmedSubmission` for why a re-voice must never call this a second time.
   *
   * `meta.persona` is deliberately NOT set here. It is the one key that may differ between two
   * files of the same analysis, and `submitVoice` is the only thing that writes it.
   */
  function buildConfirmedSubmission(): ConfirmedSubmission {
      const subjectPayload: Record<string, unknown> = { condition };
      // Override provenance (board doc §2 "Item A"): a field the human corrected away from its
      // as-pulled source value rides along as `_overrides` — the source/value fields above stay
      // the plain corrected value (so the engine recomputes off it today, no backend change
      // needed for that), while `_overrides` is a forward-compatible, clearly-labeled proposal for
      // Session 2's `agent-adjusted` provenance tag, which does not exist server-side yet.
      const subjectOverrides: Record<string, SubjectOverrideEntry> = {};
      // WHERE EACH FIGURE CAME FROM, SENT WITH IT (2026-09-25, owner: the 2924 Rosedale brief cited
      // no CAD and printed a typed garage count like a county fact). The grid has always known each
      // row's source; the submit used to drop it. A row the agent typed or corrected is theirs, and
      // says so; a sourced row sends its tag, which the server keeps only where it can prove it
      // (`amb_report._verified_subject_field_sources`). An untagged row sends nothing.
      const subjectFieldSources: Record<string, string> = {};
      for (const [key, field] of Object.entries(grid)) {
        if (field.checked && field.value != null) {
          subjectPayload[key] = field.value;
          const src = field.edited ? "You entered" : field.sourceTag;
          if (src) subjectFieldSources[key] = src;
        }
        const ov = subjectOverrideFor(field);
        if (ov) subjectOverrides[key] = ov;
      }
      // THE BATHROOM ADJUSTMENT NEVER FIRED FROM THIS FLOW (2026-08-04, owner: "we also do not
      // have an adjustment for bathrooms... shouldnt the subject have had an adjustment for having
      // more bathrooms than the comparables").
      //
      // `engine.amb._lines` gates BOTH bath lines on `subj.baths_known`, and nothing in this
      // client has ever set that flag — it was written for the legacy `web/index.html` intake,
      // which sets it when a bath value is read. So on every /next report the subject's extra
      // bathroom was worth exactly $0, silently: no line, no set-aside, no disclosure.
      //
      // BOTH COUNTS, OR NEITHER. The flag is derived here rather than assumed, and only when the
      // agent has confirmed the full AND the half count. A confirmed 3 full with the half row left
      // blank does NOT mean zero half baths — it means nobody said — and the engine is explicit
      // that an unknown bath must never become a zero-difference adjustment (it halts rather than
      // guess). Deriving the flag from the full count alone would turn every blank half-bath row
      // into a silent $0 claim, which is the exact failure this codebase refuses.
      //
      // The subject step now says this in words next to the two rows, so an agent who wants the
      // bathroom difference priced knows what to fill in.
      const _fullB = grid.full_baths?.checked ? grid.full_baths.value : null;
      const _halfB = grid.half_baths?.checked ? grid.half_baths.value : null;
      if (typeof _fullB === "number" && typeof _halfB === "number") {
        subjectPayload.baths_known = true;
      }

      // `isUsableComp`, not a bare `_selected` (2026-07-31). The grid can now hold a closed sale
      // that is real and correctly read but still waiting on the sold price its file never printed.
      // `ConfirmGrid` disables the checkbox on such a row and `ReadinessGate` won't let Generate
      // arm, so this filter should never actually remove anything — it is here because
      // `engine.amb.validate_comp` treats a missing price on a closed sale as FATAL, and a UI
      // regression that let one through would cost the agent a full round trip and a halt instead
      // of a disabled box. Two cheap guards on the same rule, deliberately.
      // ⚠️ `index` NUMBERS THE SUBMITTED COMPS, AND ONLY NOW ACTUALLY DOES (2026-08-10).
      //
      // These two lists are a pair: `comps[index]` in the payload is the property that
      // `_overrides.comps[].index` is talking about. The override list used to be built by
      // filtering to the usable comps that carry a `_raw` snapshot and then numbering THAT list —
      // so with a first comp the agent never edited and a second one they did, the second comp's
      // corrections were filed against index 0, i.e. attributed to a different property. Nothing
      // consumes `_overrides` server-side yet (see `CandidateComp._raw`: it is a declared seam for
      // Session 2's `agent-adjusted` tag, not a live contract), which is the only reason this has
      // never mis-labelled a report — it would have the moment anything read it.
      //
      // Numbering the SUBMITTED list once and dropping the entries with nothing to say keeps the
      // two in step by construction rather than by two filters agreeing.
      // ⚠️ THE ORDER OF THIS ARRAY IS THE ORDER THE BRIEF PRINTS (2026-09-08, owner).
      //
      // `amb_build.py:4080` builds its display order by filtering the SUBMITTED comps to the ones
      // the engine kept — it never re-sorts — so `C-1 / C-2 / C-3`, Focus's and Panorama's column
      // order and Folio's plates all follow this line. Which is exactly why the agent's ladder is
      // applied HERE, once, rather than anywhere downstream: the decision they made on the review
      // screen and the order the client reads are the same fact, and there is one place that is
      // true.
      //
      // `displayOrder` IS THE SAME FUNCTION `ConfirmGrid` PAINTS WITH, deliberately. The server
      // refuses the record when the ladder it is sent does not describe the comparables it is sent
      // beside (`comp_primacy.validate`, refusal 3), and two spellings of "lead first, then the
      // rest" is one build away from a brief whose note and whose first column disagree.
      //
      // The index pairing below still holds. `compOverrides` numbers the SUBMITTED list, and it is
      // built from that same reordered array in the same pass — so a correction still lands on the
      // property it was made against, whatever order the sales are printed in.
      const rankedUsable = comps.filter(isUsableComp);
      const usableComps = displayOrder(rankedUsable, rankedUsable.map((_, i) => i))
        .map((i) => rankedUsable[i]);
      const compsPayload = usableComps.map(({ _selected, _raw, _lead, _outside_criteria, _home_type_unstated, _lot_unmatched, _lot_unstated, _lot_computed_sf, _lot_computed_from, _lot_computed_note, _site_acres, _site_tag, _site_how, _site_parcels, _site_display, _site_parcel_sources, _site_unstated, _site_unstated_text, _site_unlisted, _held_mls, _quality_flags, ...rest }) => (_held_mls ? markHeld(rest) : rest))
        // HOTFIX 2026-11-03: every comp sent is one the agent left ticked on the review they just
        // pressed Generate on, so it IS reviewed and selected. A draft saved before the fix carries a
        // pre-ticked search row as `confirmed: false`, which the server refuses with a 400.
        .map(confirmTicked);
      const compOverrides = usableComps
        .map((c, i) => ({ index: i, fields: c._raw }))
        .filter((o): o is { index: number; fields: NonNullable<typeof o.fields> } => !!o.fields);
      // WHAT THE AGENT DECIDED, BESIDE WHAT THE ANALYSIS HAD RANKED. `rankedUsable` is the order
      // the rows arrived in (search merge order on the cascade path, not `rank_comps`), so `was` is read off it rather
      // than asserted. `null` when the ladder was left alone, and the key is then omitted — an
      // empty overlay and no overlay are the same thing to the engine.
      const compPrimacy = primacyRecord(usableComps, rankedUsable);
      // Drop every blank/zero entry before sending. `manual_value_adjustments` already ignores an
      // unparseable field, but an explicit null on the wire and a field the agent never filled in
      // are different claims, and only one of them is true here.
      const cleanAdjustments: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(valueAdjustments)) {
        if (v === null || v === undefined || v === "") continue;
        if (typeof v === "number" && !Number.isFinite(v)) continue;
        cleanAdjustments[k] = v;
      }
      // An amount with no direction is discarded by the engine, so it is dropped here too rather
      // than travelling as a number that looks like it will be applied and never is. The panel
      // says so on screen; this makes the payload agree with the screen.
      if (cleanAdjustments.lotview_value != null && !cleanAdjustments.lotview_dir) {
        delete cleanAdjustments.lotview_value;
      }
      if (cleanAdjustments.lotview_dir != null && cleanAdjustments.lotview_value == null) {
        delete cleanAdjustments.lotview_dir;
      }
      const activesClaim = activesSearchClaim({
        mls: cascade?.mls, listingCount: listings.length,
        // Not `job`: `job` is replaced by each build, and this payload is compared with the stored
        // confirmed one to decide whether the report is stale (`editedSinceBuild`). `extractJobId` is
        // set once and persisted with the draft, so it survives a build and a refresh.
        fileListingRows: extractJobId ? listings.length : 0,
      });
      return {
        subject: subjectPayload,
        comps: compsPayload,
        tier,
        meta: {
          // `subjectAnchor`, not `addressLine` — see its declaration. This is the string the
          // deliverable prints in its header AND the one the Street View subject-photo fallback is
          // fetched against, so a subject resolved from Cotality or from the file must reach it.
          ...(subjectAnchor ? { address_line: subjectAnchor } : {}),
          // Where the home is, from its own county sheet, so the report can pin it (Lane 5).
          ...(subjectLocality && subjectPinQuery !== subjectAnchor ? { city_state_zip: subjectLocality } : {}),
          // The hero's "competing homes now" tile. A count is sent only when someone LOOKED: the
          // listings the agent sees, or a search that ran and found none (`actives: []`). A bare
          // `actives_now: 0` with no search behind it printed "0 on the market now" on a report
          // whose competing-homes search never ran (937 Field Trail, 2026-10-24). Unknown is a dash.
          ...(listings.length || activesClaim?.searched ? { actives_now: listings.length } : {}),
          ...(activesClaim ? { actives_search: activesClaim } : {}),
          ...(!listings.length && activesClaim?.searched ? { actives: [] } : {}),
          // THE COMPETING LISTINGS THEMSELVES, not just the count. `CompetingListings.tsx` tells the
          // agent these "appear in the report as the competition your listing will launch against",
          // and until now the report showed a number and nothing else — the UI promised something
          // the renderer did not do. These are NOT comparables and never enter the valuation: they
          // travel in `meta`, never in `comps`, and the engine reads them only to render §4.
          ...(listings.length
            ? {
                // Only what the agent chose to show. `_selected !== false` rather than
                // `_selected === true` so a listings array from an older saved draft — which has
                // no flag at all — still sends everything, exactly as it did before.
                actives: listings
                  .filter((c) => c._selected !== false)
                  .map(({ _selected, _raw, _outside_criteria, _home_type_unstated, _lot_unmatched, _lot_unstated, _lot_computed_sf, _lot_computed_from, _lot_computed_note, _site_acres, _site_tag, _site_how, _site_parcels, _site_display, _site_parcel_sources, _site_unstated, _site_unstated_text, _site_unlisted, _held_mls, ...rest }) => (_held_mls ? markHeld(rest) : rest)),
              }
            : {}),
          ...(Object.keys(subjectFieldSources).length ? { subject_field_sources: subjectFieldSources } : {}),
          // THE SUBJECT CHANGE LEDGER (owner design, 2026-10-04). Omitted when the subject was
          // never switched. The server stamps who; the client sends only what it witnessed.
          ...(subjectChangeLog.length ? { subject_change_log: subjectChangeLog } : {}),
          // THE LOCK OF THE PICKED COUNTY RECORD. `cad_picker.verified_cad_pick_sources` re-reads
          // that record by prop_id at the same dataset version and keeps a field's CAD tag only
          // where the value still matches — without this the server can prove nothing, and every
          // CAD-picked figure would print untagged. A claim, never the proof.
          ...(subjectCadPick?.lock ? { cad_pick: subjectCadPick.lock } : {}),
          // The agent's confirmed photo, when they chose one; the server gates it and labels it theirs.
          ...(confirmedSubjectPhoto ? { subject_photo: confirmedSubjectPhoto } : {}),
          ...(Object.keys(subjectOverrides).length || compOverrides.length
            ? { _overrides: { subject: subjectOverrides, comps: compOverrides } }
            : {}),
          ...(compPrimacy ? { comp_primacy: compPrimacy } : {}),
          // STEP 2'S TWO INPUTS, ON TWO DIFFERENT WIRES (2026-08-01).
          //
          // `value_adjustments` is read by `engine.amb.manual_value_adjustments` and moves the
          // headline figures as a disclosed, post-reconciliation overlay, each line labelled with
          // the agent as its source. `extra_context` reaches the narrative drafter and moves
          // nothing — the prompt forbids it from introducing a number or a fact the evidence
          // doesn't already carry, and the QA gate would catch it if it tried.
          //
          // Both are omitted entirely when empty rather than sent as {} / "": an empty overlay and
          // no overlay are the same thing to the engine, and a key that is always present makes a
          // stored payload look like the agent answered a question they were never asked.
          ...(Object.keys(cleanAdjustments).length ? { value_adjustments: cleanAdjustments } : {}),
          // A HALF-FILLED OVERRIDE IS NOT AN OVERRIDE. Same rule `cleanAdjustments` applies to
          // lot/view: a rate with no direction, or a direction with no rate, says nothing, and
          // sending it would make the engine choose between an incomplete instruction and the
          // figure it derived from the sales. Omitted entirely, the derived rate stands.
          ...(marketConditions.rate_pct_per_month != null &&
              marketConditions.rate_pct_per_month !== 0 &&
              (marketConditions.direction === "rising" || marketConditions.direction === "falling")
            ? { market_conditions: marketConditions }
            : {}),
          ...(extraContext.trim() ? { extra_context: extraContext.trim() } : {}),
          // WHO THE BRIEF IS ADDRESSED TO, WHEN SOMEONE ACTUALLY SAID (2026-08-05).
          //
          // Same omit-when-empty rule as the two above, and here it is load-bearing rather than
          // tidy. `amb_build` defaults `prepared_for` to the literal "Property owner", which
          // `_owner_first_name` reads as "no name given" and turns into an ABSENT `owner_name` in
          // the narrative packet — the state the spec's LEAD contract calls "do not invent, guess,
          // or borrow a name". Sending "" or a key the agent never filled in would be this client
          // asserting something about the homeowner on no evidence, which is the whole defect this
          // field exists to close. Trimmed and otherwise untouched: `_owner_first_name` owns what
          // counts as a name, and a second copy of that rule here would only drift.
          ...(preparedFor.trim() ? { prepared_for: preparedFor.trim() } : {}),
          // The CONFIRMED owner of record, for the "Prepared for" line only; the server prints it
          // only if its own reading of the sheet named the same owner.
          ...(useOwnerOfRecord && cadSource && cadIdentity?.owner_name
            ? { prepared_for_record: cadIdentity.owner_name } : {}),
        },
      };
  }

  /**
   * ONE BUILD PER VOICE (2026-08-04, unchanged in substance). The payload is identical for every
   * voice except `meta.persona`, so the figures a reader compares across files are provably the
   * same figures — they were confirmed once, on one screen, and sent unchanged. This function is
   * the ONLY place `persona` is written, and it never touches anything else on the payload, which
   * is what makes "identical except the voice" a property of the code rather than a habit.
   *
   * `sourceJobId` IS THE EXTRACT JOB, NOT `job` (2026-08-07). See `extractJobId`: `job` is the
   * build job by the time a second voice is asked for, and naming a build job here silently strips
   * every server-verified comp photo from the new report.
   */
  function submitVoice(payload: ConfirmedSubmission, voice: PersonaId) {
    return submitConfirmed({
      subject: payload.subject,
      comps: payload.comps,
      tier: payload.tier,
      // A photo the agent set on the finished report travels with every later voice; the payload
      // was built before it existed. Nothing else about the payload changes.
      meta: { ...payload.meta, persona: voice,
              ...(confirmedSubjectPhoto ? { subject_photo: confirmedSubjectPhoto } : {}) },
      sourceJobId: extractJobId ?? undefined,
    });
  }

  async function handleSubmit() {
    // THE LAST DOOR (2026-08-07). Both step gates already route through `requestForward`, so a
    // moved subject cannot normally reach this button with the old comparables still ticked. This
    // is the same two-guards-on-one-rule posture `isUsableComp` gets in the payload builder: a
    // restored draft, or any path added later that reaches step 4 without passing a gate, must not
    // be able to price one home off another home's sales. It asks rather than refusing, and sends
    // them to the step that can fix it.
    if (editImpact.invalidates) {
      setPendingStep("comps");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      /* Built ONCE, here, and kept. Every voice in this run — and every voice asked for later from
         the finished report — is sent this same object.

         WHAT THIS LOOP DOES AND DOES NOT DO (corrected 2026-08-10). It used to claim the voices are
         "submitted SEQUENTIALLY rather than in parallel: five simultaneous model calls is a
         thundering herd against the same instance." That is not true, and believing it sends the
         next person looking for a concurrency problem that is already handled.

         `POST /api/amb/jobs` creates the row and returns — `amb_job_direct` ends at `_start()`,
         which is `threading.Thread(target=_run_job, daemon=True).start()` (amb_report.py). The
         await below therefore resolves in milliseconds, before any model call happens. What is
         sequenced is five HTTP requests; what runs is five concurrent builds on one instance.

         So if the five-voice run needs a concurrency limit, it needs a SERVER-SIDE one — a
         semaphore around `_run_job` — and awaiting the POSTs in a row buys nothing. Do not add one
         before reading the actual `job.reason` from a failed run: a limit of 1 turns a two-minute
         run into ten, which is a product decision, and the fix is different if the jobs died on an
         engine halt rather than on memory.

         The FIRST job is the one the flow follows and polls, exactly as a single-voice run always
         has. The rest are recorded as siblings and their ids drive the download set. */
      // NTREIS MLS (Release 1.1): each selected MLS comp gets its own listing photo, fetched only
      // now, for the comps actually chosen. A payload with no MLS row is returned unchanged.
      const payload = await withMlsPhotos(buildConfirmedSubmission());
      setConfirmedPayload(payload);
      setPayloadWithheld(false);
      const { job: j, notice: n } = await submitVoice(payload, personas[0]);
      const siblings: { persona: PersonaId; jobId: string }[] = [{ persona: personas[0], jobId: j.id }];
      for (const voice of personas.slice(1)) {
        try {
          const { job: sj } = await submitVoice(payload, voice);
          siblings.push({ persona: voice, jobId: sj.id });
        } catch {
          /* One voice failing must not cost the others. The download screen lists what exists and
             names what is missing, rather than silently presenting a short set as complete. */
        }
      }
      setPersonaJobs(siblings);

      // The draft is keyed by job id and the URL is about to point at the BUILD job — copy it
      // across, or a reload on a failed build loses the reviewed grid and with it the "Back to
      // review" exit, while the data sits unreachable under the extract job's key.
      // Carry the WHOLE draft across, not a subset. This copy used to drop `listings`,
      // `priorSales`, `gaps` and now the two upgrade inputs — so a reload on a failed build came
      // back to a review missing the competition set, the subject's own prior sale and whatever the
      // agent had typed about the property, with no sign any of it had ever been there. Parked at
      // the reviewing step they'd finished on, so "Back to review" lands where they left.
      // The three second-build fields are written from the LOCALS, not from state: `setPersonaJobs`
      // and `setConfirmedPayload` above have not been flushed yet, and a draft that carried the
      // previous run's siblings would be worse than one carrying none.
      saveDraft(j.id, {
        grid, comps, condition, addressLine, tier, personas, listings, priorSales, gaps,
        reconcileNotes, subjectNotice, valueAdjustments, marketConditions, extraContext, preparedFor, step1Grid,
        personaJobs: siblings, extractJobId, confirmedPayload: payload, cascade,
        step: "review",
      });
      setJob(j);
      setNotice(n);
      // §6 (gap closed 2026-10-02): a Generate was just ACCEPTED — the count every open surface
      // (the pill, the tile, any other tab's in-flow line) is showing just moved, so they all
      // re-fetch together rather than drifting until their next independent remount.
      refreshUsage();
      setStep("build");
      // UI-009: the build job id replaces the extract job id in the URL, so a refresh on the
      // report screen comes back to the report, not to a blank intake.
      navigate(`/cma/${j.id}`, { replace: true });
      rehydratedFor.current = j.id;
      if (!BUILD_TERMINAL.includes(j.status)) pollJob(j.id, () => {}, BUILD_TERMINAL, setSubmitError);
    } catch (e) {
      // §6: a refused Generate is the race case this gap closes — proactive disabling thought
      // there was a slot, and the server is what actually decided there was not.
      if (isLimitReached(e)) {
        setLimitRefusal(e.body.message);
        refreshUsage();
        return;
      }
      setSubmitError(buildStartFailure(e, job?.id));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * THE SAME ANALYSIS, IN ANOTHER VOICE — WITHOUT RE-RUNNING ANY OF IT (2026-08-07, owner request:
   * "we need to allow this to be so much easier than just rerunning the entire process from
   * beginning to end").
   *
   * WHAT THIS COSTS, honestly: one build. `_run_job`'s "direct" mode consumes the `subject` and
   * `comps` posted here and opens at `compute+draft` — it never re-reads a file, never re-runs OCR
   * or the extractor, and never re-asks a provider. So the whole extraction half of the workflow is
   * already skipped; what is left is the model call that writes the prose, which is irreducible for
   * a genuinely different voice. The old way to get here was to upload the file again.
   *
   * WHAT THIS MUST NOT DO: rebuild the inputs. `confirmedPayload` is re-sent unchanged and only
   * `meta.persona` differs (see `submitVoice`), which is the only reason two voices of one analysis
   * can be trusted to carry the same reconciled price. Deriving inputs from the finished report is
   * not merely discouraged, it is impossible — `data.grid.comps` holds display names and formatted
   * strings — and deriving them from the live grid would let an edit made since Generate move the
   * headline between two files the agent believes are the same numbers in two registers.
   *
   * The new job is APPENDED to `personaJobs`, so the agent ends up holding both voices rather than
   * trading the first for the second. Two voices means the screen becomes the download set, for the
   * reason `PersonaDownloads` states: an inline report can only render one narrative, and leaving
   * voice 1 on screen while voice 2 writes would silently promote it to "the" report.
   */
  /* THE NEW VOICE OPENS IN A NEW TAB (2026-08-14, owner request).
   *
   *   "when an agent is on this screen in review or edit mode, and the agent clicks a different
   *    narrative it should open that narrative in a new browser tab so they dont lose what they
   *    are working on or the existing narrative"
   *
   * That is a correctness fix, not a convenience. Before this, a second voice was appended to
   * `personaJobs`, and `personaJobs.length > 1` swaps this whole screen for `PersonaDownloads` —
   * so the click took away the report being read AND any narrative edits typed into it, with no
   * warning and no undo. The agent asked for a comparison and was charged the thing they were
   * comparing against.
   *
   * THE TAB IS OPENED SYNCHRONOUSLY, BEFORE THE AWAIT. A `window.open` issued after an awaited
   * fetch has lost the click's user activation and is blocked as a pop-up in every current
   * browser. It is opened blank first and pointed at the job once the id exists.
   *
   * `noopener` is NOT passed, deliberately: with it, `window.open` returns null by specification
   * and there is no handle left to navigate. The target is same-origin, so there is no
   * reverse-tabnabbing exposure to trade away, and `opener` is cleared once the tab is pointed.
   *
   * A BLOCKED POP-UP IS SAID OUT LOUD. The job is built either way — it is a model call the agent
   * has already spent — so swallowing the failure would leave them with a report they paid for and
   * no way to reach it. `revoicedAway` keeps the id and the screen offers the link.
   */
  async function handleRevoice(voice: PersonaId) {
    // `editedSinceBuild` ALSO GUARDS THE HANDLER, not only the control (2026-08-07). The screen
    // stops rendering the voice buttons the moment the live inputs diverge from the payload that
    // built the report, so this is unreachable through the UI — and it is exactly the kind of rule
    // that must not live only in a render branch. A second file written from figures the agent has
    // already corrected, and handed to them as current, is the worst thing this feature could do.
    if (!confirmedPayload || revoicing || editedSinceBuild) return;

    // Opened here, in the click, and not one line later. See the note above.
    const tab = window.open("", "_blank");
    if (tab) {
      // Static markup only — nothing here interpolates anything. A blank white tab beside a
      // spinner on the original reads as a browser that has hung.
      tab.document.write(
        "<!doctype html><title>Writing your report…</title>" +
          '<body style="font:15px/1.6 system-ui,sans-serif;color:#334;padding:48px">' +
          "Writing this brief in the voice you chose. This tab will show it when it is ready — " +
          "the report you were reading is untouched in the tab you came from."
      );
      tab.document.close();
    }

    setRevoicing(voice);
    setRevoiceError(null);
    setRevoiceBlocked(null);
    try {
      const { job: sj } = await submitVoice(confirmedPayload, voice);

      // THE NEW TAB GETS THE SAME CONTEXT THIS ONE HAS. Drafts are keyed by job id, so without
      // this the new tab rehydrates a report with no reviewed grid behind it: no "Back to review",
      // no re-voice, and no way back to the comparables except starting over from the file.
      // `revoicedAway` is deliberately NOT copied — each tab records the voices IT opened.
      saveDraft(sj.id, {
        grid, comps, condition, addressLine, tier, personas, listings, priorSales, gaps,
        reconcileNotes, subjectNotice, valueAdjustments, marketConditions, extraContext,
        preparedFor, step1Grid, extractJobId, confirmedPayload, compsAnchor, cascade,
        personaJobs: [], step: "review",
      });

      const href = `/cma/${sj.id}`;
      if (tab && !tab.closed) {
        tab.opener = null;                       // same-origin, but there is nothing to gain by keeping it
        tab.location.replace(href);
      } else {
        // Blocked, or closed while it was writing. The job exists and was paid for; say where.
        setRevoiceBlocked({ persona: voice, jobId: sj.id, href });
      }
      // Recorded whether or not the tab opened, so the voice is never offered as new twice.
      setRevoicedAway((prev) => [...prev, { persona: voice, jobId: sj.id }]);
    } catch (e) {
      if (tab && !tab.closed) tab.close();       // never leave a tab claiming to be writing something that is not
      setRevoiceError(buildStartFailure(e, job?.id, "voice"));
    } finally {
      setRevoicing(null);
    }
  }

  /**
   * WHAT MOVING ON WOULD COST, RIGHT NOW (2026-08-07). Recomputed every render from the anchor and
   * the live subject; `EditImpact` owns the rule and the sentence, this owns only the inputs. See
   * that file for the full matrix and for why "a different property" is `addressesAgree` rather
   * than string equality.
   *
   * `invalidates` is false on every ordinary render — no comps yet, or the subject has not moved —
   * so the whole mechanism is invisible until the one case it is for.
   */
  const editImpact = describeSubjectChange({
    compsAnchor,
    subjectAnchor,
    comps: comps.length,
    listings: listings.length,
    priorSales: priorSales.length,
  });

  /**
   * THE ONE DOOR EVERY FORWARD MOVE GOES THROUGH.
   *
   * Backward moves are never guarded: going back changes nothing, and a prompt on the way to fix a
   * typo is how a warning becomes noise. Forward moves are, because forward is where an invalidated
   * comparable set would start being used again — by the comps step's gate, by the review summary,
   * and finally by the build.
   */
  function requestForward(target: Step) {
    if (editImpact.invalidates) {
      setPendingStep(target);
      return;
    }
    setStep(target);
  }

  /**
   * The answer to that prompt, and the ONLY place downstream work is discarded.
   *
   * IT CLEARS EXACTLY WHAT BELONGS TO THE OLD ADDRESS AND NOTHING ELSE. The three tables are sales
   * of, or competition for, a home this analysis is no longer about. `confirmedPayload` and
   * `personaJobs` go with them because they are the last build's inputs and outputs, and offering
   * "rebuild this in another voice" against a property the agent has just left is the same defect
   * one step later.
   *
   * WHAT SURVIVES, DELIBERATELY: the subject grid (it is the new home's, that is why it changed),
   * the condition ladder, the value adjustments, the extra context, the homeowner's name, and both
   * disclosure lists. `gaps` and `fileStatus` are statements about the FILE — how each one was
   * read, which rows came off the market without selling — and the file has not changed. A note is
   * appended rather than replacing them, so the reading notes carry a record of this happening
   * instead of the agent having to remember which button they pressed.
   */
  function confirmInvalidatingEdit() {
    const target = pendingStep;
    const from = editImpact.from;
    const to = editImpact.to;
    setComps([]);
    setListings([]);
    setPriorSales([]);
    setCompsAnchor(null);
    setConfirmedPayload(null);
    setPayloadWithheld(false);
    setPersonaJobs([]);
    setRevoiceError(null);
    addReconcileNote(
      `You changed the subject from ${from} to ${to}. The comparable sales, competing listings and ` +
        `prior sales that had been read for ${from} were cleared — none of them were used for ` +
        `${to}. Everything you had entered about the home itself was kept.`
    );
    setPendingStep(null);
    if (target) setStep(target);
  }

  /**
   * HAS ANYTHING CHANGED SINCE THE REPORT ON SCREEN WAS BUILT?
   *
   * Asked by building the payload the flow would send RIGHT NOW and comparing it, whole, against
   * the one that actually built the report. Not a dirty flag on each input: a flag has to be set by
   * every writer and a writer added later silently doesn't set it, whereas this is defined as
   * exactly the question it answers — "would a build started now send different bytes?" — and
   * cannot drift from `buildConfirmedSubmission`, because it IS `buildConfirmedSubmission`.
   *
   * Both sides are built by the same function in the same order, so key order is not a variable.
   * `meta.persona` is absent from both by construction (see `ConfirmedSubmission`), which is what
   * makes a re-voice — the one change that is allowed to differ — not read as an edit.
   *
   * WHAT IT IS FOR: a re-voice re-sends the stored payload unchanged, on purpose. That is right
   * while the stored payload is still the truth and quietly wrong the moment it isn't, so when this
   * is true the voice buttons are replaced by the one move that isn't wrong — regenerate. Nothing
   * this flow renders can start a build from figures the agent has already corrected.
   */
  const editedSinceBuild =
    !!confirmedPayload &&
    JSON.stringify(buildConfirmedSubmission()) !== JSON.stringify(confirmedPayload);

  /** Whether there is a REVIEW to go back to — a read file, not merely a subject.
   *
   * THE `!!job` HALF IS LOAD-BEARING, NOT LEFTOVER (added 2026-08-05; this note added the same day
   * so it is not read later as a stray guard and deleted). `Object.keys(grid).length > 0` alone
   * stopped being this question the moment step 1 began writing into this same grid: a successful
   * address lookup fills `grid` while the agent is still standing ON the intake screen with no file
   * uploaded, so the banner "Your review of the last file is still here, untouched" would have
   * appeared underneath the lookup they had just run, offering "Back to your review" — forward, to
   * a review of a file that does not exist. `job` is non-null only once an extract has actually been
   * started, which is the real test: a review needs a file, and a file means a job.
   *
   * Both halves are still needed. `job` alone would be true mid-extract, before there is anything to
   * go back TO.
   *
   * AND A REVIEW NO LONGER NEEDS A FILE (2026-08-11, the source cascade). "A review needs a file,
   * and a file means a job" was true of every path that existed when it was written, and stopped
   * being true the day an agent could reach step 2 from an address alone — there is no extract job
   * on that path and there never will be. Left as it was, the step rail would have been dead on the
   * one route the cascade exists to provide: the agent gets to step 2 and cannot get back to step 3
   * after a detour, which is NAVIGATION rule 6's dead-end wearing a different hat. A completed
   * resolve is the same evidence a completed extract is — something downstream exists to return to
   * — so either satisfies the first half. The `grid` half is untouched and still does its job. */
  const hasReview = (!!job || !!cascade) && Object.keys(grid).length > 0;

  /** A FINISHED REPORT THIS FLOW CAN PUT BACK ON SCREEN WITHOUT BUILDING ANYTHING (2026-09-07).
   *
   * `job.status === "done"` and nothing else: `job` is the build job by the time a report exists
   * (`handleSubmit` sets it), the report screen renders from that same job, and `setStep("build")`
   * is the whole of the return trip — no submit, no model call, no download re-requested.
   *
   * Deliberately NOT `personaJobs.length > 0`: a multi-voice run's siblings are ids, and one of
   * them being finished says nothing about whether THIS tab has a report on it. */
  const reportToReturnTo = job?.status === "done";

  /**
   * EVERY STEP IS A DOOR (2026-09-07, owner: "fix the ability to go anywhere into the process of
   * creating a file ... this is terrible").
   *
   * WHAT WAS WRONG, and it is worth stating plainly because the previous version of this comment
   * was a careful defence of it. The rail offered a step only when `hasReview` — a job AND a
   * non-empty grid — so until an agent had run an extract and confirmed something, EVERY node was
   * an inert `<span>`. Standing on step 1 with an address typed, "Property" and "Comps" were
   * unclickable and said nothing about why. The reasoning was sound in its own terms: there is
   * nothing on those steps to go back to. But a person does not experience a locked control as
   * "there is nothing there yet"; they experience it as a product that will not let them look.
   *
   * NAVIGATION IS NOT AUTHORISATION, and conflating the two is what produced the lock. Moving to a
   * step shows you that step. It does not arm anything: `ReadinessGate` gates the Generate button
   * on its own conditions and is untouched by this, so arriving at step 4 with nothing confirmed
   * gets you the readiness gate LISTING WHAT IS MISSING — which is the single most useful screen a
   * confused agent can be shown, and it was the one screen the rail refused to open.
   *
   * Every step also already renders honestly with no data behind it. Step 2 says so in its own
   * comment: "`step1Grid` is null when step 1 never produced a subject, in which case this IS the
   * first time these fields are asked for and every row renders as a question." Step 3 draws an
   * empty comparable table. Step 1 renders the "your review is still here, untouched" banner when
   * there is a review and the plain intake form when there is not.
   *
   * THE REPORT IS THE ONE EXCEPTION, and it is not a lock — it is a fact about the sequence. The
   * report is the OUTCOME of step 4, not a step you work in, and `NarrativeReportScreen` with no
   * job renders "Starting your report … Setting up the build", which would be a sentence about
   * work that is not happening. So that node stays inert until a report exists AND SAYS WHY on
   * hover and to a screen reader, through `whyNot` below — because the actual defect being fixed
   * here is not "the node was inert", it is "the node was inert and silent".
   */
  const canGoToStep = useCallback(
    (target: ShellStep) => (target === "build" ? reportToReturnTo : true),
    [reportToReturnTo]
  );

  /** WHY A NODE IS NOT A DOOR, in the words a person can act on. Only ever asked about a node the
   *  rail is about to draw as inert, so a returned string is always the answer to "why can I not
   *  click this". */
  const whyStepIsClosed = useCallback(
    (target: ShellStep) =>
      target === "build"
        ? "The report is what step 4 produces — press Generate there and you will land here."
        : undefined,
    []
  );

  /** The rail's own click handler.
   *
   * `build` CAN arrive here now, and only when `canGoToStep` allowed it — which is only when a
   * finished report exists. The guard stays for the one case that would otherwise put the flow on
   * a report screen with nothing to render: a caller that did not consult the gate. */
  const handleStepSelect = useCallback((target: ShellStep) => {
    if (target === "build" && !reportToReturnTo) return;
    setStep(target);
  }, [reportToReturnTo]);

  // ---- render -------------------------------------------------------------
  // UI-001: every branch below renders INSIDE the shell. The old early return at the intake step
  // is exactly what made step 1 chrome-less.

  /* THE QUESTION COMES BEFORE EVERYTHING ELSE ON THE SCREEN IT WAS ASKED FROM (2026-08-07).
   *
   * One branch rather than a block bolted onto each of the four steps: the prompt is asked from
   * three different screens and a copy on each is three places for the wording — and the two exits
   * — to drift. It renders in the shell at the step the agent is standing on, so the rail still
   * says where they are, and it deliberately does NOT pass `onStepSelect`: while an unanswered
   * question is on screen the only two ways out are the two buttons, and a breadcrumb click that
   * silently abandoned it would leave `pendingStep` set behind their back.
   *
   * Nothing has been cleared at this point. `confirmInvalidatingEdit` is the only thing that
   * clears, and it runs from the second button. */
  if (pendingStep && editImpact.invalidates) {
    return (
      <AppShell step={step} content="prose">
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Before we go on</h1>
        <InvalidationPrompt
          impact={editImpact}
          onConfirm={confirmInvalidatingEdit}
          onCancel={() => setPendingStep(null)}
        />
      </AppShell>
    );
  }

  if (step === "intake") {
    /* STEP 1 IS A CONTROL PANEL, NOT A DOCUMENT (2026-08-11). It is three stacked cards — the tier
       picker, the address lookup with its results list, the dropzone and its file chips — and the
       measurement the owner took of the "extremely narrow field" was taken on THIS screen: 880px of
       content inside 632px of empty margin at 1512. Its paragraphs, and the address input itself,
       carry their own measure (see `IntakeScreen` and `SubjectAddressStep`), so the cards get the
       room the monitor has and the sentences keep their line length. */
    return (
      <AppShell step="intake" content="full" onStepSelect={handleStepSelect} canGoToStep={canGoToStep}
              whyStepIsClosed={whyStepIsClosed}>
        {mismatch ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Before we open the review</h1>
            <AddressMismatchPrompt
              mismatch={mismatch}
              onUseTyped={() => {
                if (!job) return;
                // ANSWERING THE QUESTION IS NOT THE SAME AS BEING TOLD WHAT IT COST (2026-08-05).
                // The agent has just said "the report is about the address I gave you", and the
                // consequence — the uploaded sheet's own property record is not used for the
                // subject, only its sales are — is a thing they should still be able to read on the
                // next screen and again in the reading notes, rather than having to remember which
                // button they pressed. Only when the file actually named a property; a comps-only
                // file has no record to decline.
                if (mismatch.fromFile) {
                  const note =
                    `The sheet you uploaded is a property record for ${mismatch.fromFile}, which is ` +
                    `not the property you chose (${mismatch.typed}). Its sales are used as comparables; ` +
                    `none of its property details were taken for your home. If that sheet IS your ` +
                    `subject, go back a step and change the address.`;
                  setSubjectNotice(note);
                  addReconcileNote(note);
                }
                // The human's address wins, and the file's subject record does NOT ride along with
                // it — that would be borrowing another property's facts under this address.
                applyExtract(job, { address: mismatch.typed, condition: "average" });
              }}
              onUseFile={() => {
                if (!job) return;
                const fileAddr = mismatch.fromFile;
                if (fileAddr) setAddressLine(fileAddr);
                // THE ANSWER HAS TO ACTUALLY TAKE (2026-08-05). Step 1 now writes into this same
                // grid, so by the time this button exists the grid can already hold a CONFIRMED
                // subject for the other address — and `applyExtract` merges, never clobbers, which
                // would have kept every one of those confirmed fields while the agent had just said
                // the file's property is the subject. Emptying the grid first restores the meaning
                // this button has always had: the file's subject, whole, source-tagged to the file.
                // Queued before `applyExtract`'s own functional update, so it sees `{}` and rebuilds
                // rather than merging.
                setGrid({});
                // And the step-1 line is a line under a property that is no longer the subject, so
                // step 2 asks for the new one outright instead of presenting it as a diff.
                setStep1Grid(null);
                setSubjectNotice(null);
                applyExtract(job);
              }}
              onCancel={handleStartOver}
            />
          </>
        ) : phase !== "idle" ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Reading what you sent</h1>
            {/* SAY WHAT IT COSTS, BEFORE IT COSTS IT (2026-08-04). A PDF exported without a font
                character map has no readable text layer, so every page is read as an image at
                roughly 13 seconds a page. That is normal and it is slow, and a page that says
                nothing about it reads as a page that has hung. */}
            <p style={{ marginTop: 10, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
              This can take {EXPECTED_WORST_CASE}, depending on how many pages your file has and how
              it was exported. A PDF that was scanned, or exported without a readable text layer, has
              to be read a page at a time — that is the slow case, and it is working normally. You
              can leave this page open; it keeps checking.
            </p>
            {stalled && (
              <p role="alert" style={{ marginTop: 12, fontSize: 13, color: colors.caution, lineHeight: 1.5 }}>
                This job hasn&apos;t reported any progress for five minutes, which is longer than even
                a large scanned file should go silent. It may still finish — this page keeps checking —
                but if nothing moves in the next few minutes, start over with the file, or send{" "}
                hello@valubrief.ai job {job?.id ?? "(no id)"}.
              </p>
            )}
            {extractError && (
              <p role="alert" style={{ marginTop: 12, fontSize: 13, color: colors.caution, lineHeight: 1.5 }}>
                {extractError}
              </p>
            )}
            <ExtractProgress job={job} phase={phase} fileNames={fileNames} onStartOver={handleStartOver} />
          </>
        ) : (
          <>
            {/* NAVIGATION rule 6: stepping back from the review must not strand the review. A Back
                that silently destroys 34 seconds of extraction is the defect UI-001 was filed for,
                so the way forward stays visible the whole time the draft is in memory. */}
            {hasReview && (
              <div
                style={{
                  marginBottom: 18,
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: colors.accentSoft,
                  border: `1px solid ${colors.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                {/* THE BANNER SAYS WHICH HOME THE REVIEW IS FOR, ONCE THAT STOPS BEING OBVIOUS
                    (2026-08-07). "Still here, untouched" is exactly right until the agent changes
                    the address in the box above it — and then it is a reassurance about work that
                    belongs to a different property, sitting directly under the change that made it
                    so. The prompt behind the button is the decision; this is the warning, and it is
                    readable without pressing anything. */}
                {/* AND IT ONLY SAYS "FILE" WHEN THERE WAS ONE (2026-08-11). On the address-only
                    path there is no file to have a review "of", and telling an agent their review
                    of a file is still here — when they never uploaded one — reads as the app
                    describing somebody else's session. */}
                <span style={{ fontSize: 13 }}>
                  {editImpact.invalidates
                    ? `Your review is still here, but it was read for ${editImpact.from} — not for ${editImpact.to}.`
                    : fileNames.length
                      ? "Your review of the last file is still here, untouched."
                      : "Your review is still here, untouched."}
                </span>
                <button
                  type="button"
                  onClick={() => requestForward("detail")}
                  style={{ padding: "7px 12px", borderRadius: 8, border: "none", background: colors.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                >
                  Back to your review
                </button>
                {/* AND THE REPORT, WHEN THERE IS ONE (2026-09-07). Step 1 became reachable from the
                    finished report today, and a door with nothing behind it is NAVIGATION rule 6's
                    dead end wearing a different hat: without this the only way back to a report the
                    agent had already paid for was to walk the review again and press Generate,
                    which spends a second model call to arrive where they already were. Nothing here
                    rebuilds anything — the report is still in state, and this puts it back on
                    screen. */}
                {reportToReturnTo && (
                  <button className="vb-btn"
                    type="button"
                    onClick={() => setStep("build")}
                    style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: "pointer" }}
                  >
                    Back to your report
                  </button>
                )}
                <button className="vb-btn"
                  type="button"
                  onClick={handleStartOver}
                  style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgSurface, color: colors.textPrimary, fontSize: 13, cursor: "pointer" }}
                >
                  Discard it and start over
                </button>
              </div>
            )}
            {/* THE ADDRESS-ONLY ENTRY'S OWN FAILURE, ON THE SCREEN IT WAS PRESSED FROM. Worded in
                `runCascade`, and it says "we could not reach", never "no sales found" — see spec
                §6. Nothing has been ruled out, and the agent is still on a screen where the two
                other ways forward (retry, add a file) are both in front of them. */}
            {cascadeError && (
              <p role="alert" style={{ marginBottom: 14, color: colors.caution, fontSize: 13, lineHeight: 1.5, maxWidth: measure.copy }}>
                {cascadeError}
              </p>
            )}
            <IntakeScreen
              onStart={handleStart}
              busy={phase !== "idle"}
              error={extractError}
              /* Gate notices from `/status` — no provider call, no cap spend, so they can be on
                 screen before the first keystroke. Empty when there is nothing to disclose. */
              notices={noticesFromGates(cascadeStatus?.gates)}
              resolving={resolving}
              tier={tier}
              onTierChange={setTier}
              condition={condition}
              onConditionChange={setCondition}
              /* One address, one subject grid, one file list — the flow's, not a second copy this
                 screen keeps and loses on unmount. Coming back here from step 2 now finds the
                 address, the lookup result and the chosen file exactly as they were left. */
              addressLine={addressLine}
              onAddressChange={setAddressLine}
              subjectGrid={Object.keys(grid).length ? grid : null}
              onSubjectGridChange={setGrid}
              onSubjectClipResolved={setSubjectClip}
              publicRecordsDormant={publicRecordsDormant(cascadeStatus?.gates)}
              cadPick={subjectCadPick}
              onCadPickChange={setSubjectCadPick}
              onSubjectChangeLogged={logSubjectChange}
              intake={intake}
              onIntakeChange={setIntake}
              limitRefusalMessage={limitRefusal}
            />
          </>
        )}
      </AppShell>
    );
  }

  // The hidden picker behind every "add another sheet" / "upload the subject's sheet" button. Two
  // of the three reviewing steps offer that action, and each `return` below is its own tree, so it
  // is built once here rather than copied into both.
  const addFileInput = (
    <input
      ref={addFileInputRef}
      type="file"
      multiple
      accept=".csv,.tsv,.txt,.xlsx,.xls,.pdf,.docx,.pptx"
      style={{ display: "none" }}
      onChange={(e) => {
        const files = Array.from(e.target.files ?? []);
        if (files.length) handleAddFileMerge(files);
        e.target.value = "";
      }}
    />
  );

  const banners = (
    <>
      {(reviewError || submitError) && (
        <p role="alert" style={{ marginTop: 12, color: colors.caution, fontSize: 13, lineHeight: 1.5 }}>
          {submitError ?? reviewError}
        </p>
      )}
      {info && (
        <p role="status" style={{ marginTop: 12, color: colors.textSecondary, fontSize: 13 }}>
          {info}
        </p>
      )}
    </>
  );

  /** Whether the CONFIRMED grid says this home has a pool. The engine's pool overlay is a no-op
   * without it, so `UpgradesPanel` hides that field rather than rendering a box that discards what
   * is typed into it. Read from the checked value only — an unchecked row is not a fact yet. */
  const subjectHasPool = grid.pool?.checked === true && grid.pool?.value === true;

  // ---- STEP 2 — the subject's own facts, plus the two upgrade inputs -----------------------
  if (step === "detail") {
    /* THE DIVISION OF LABOUR BETWEEN STEP 1 AND STEP 2 (2026-08-05).
     *
     * Step 1 asks which home this is and shows what the public record holds about it. Step 2 used
     * to ask the identical 24 questions again, over a second piece of state, with a fresh "correct
     * anything that's wrong" instruction. It now presents THREE things: what the file (or a
     * public-records fetch, or the agent) has added since; anything required that is still not
     * confirmed; and — quietly, below, one control away from being editable — the details they
     * already confirmed.
     *
     * `step1Grid` is null when step 1 never produced a subject, in which case this IS the first
     * time these fields are asked for and every row renders as a question, exactly as before. */
    const { carried, added } = carryOver(step1Grid, grid);
    const label = (k: string) => SUBJECT_FIELD_SCHEMA.find((f) => f.key === k)?.label ?? k;
    /* The address is asked for ONCE, in its own labelled box on step 1, and printed here as the
     * heading of the screen it identifies. Kept as a row only if it somehow is not confirmed —
     * a required field must always have a control. */
    const addressSettled = isFieldConfirmed(grid, "address");
    const quietKeys = carried.filter((k) => k !== "address");
    /* The address is printed in the heading, so it is not also announced as a detail that "landed
     * since" — the agent would go looking below for a row that is deliberately not there.
     *
     * AND ONLY WHAT ACTUALLY ARRIVED FROM A SOURCE (2026-08-06, owner defect). `carryOver.added` is
     * "confirmed now, not confirmed on step 1", which includes a value the agent typed into this
     * very screen a second ago — and announcing that back to them as a detail that "has landed
     * since", with "each row says where it came from. Untick anything you don't want used", is
     * describing their own keystrokes as an arrival from somewhere else. The summary is about what
     * the FILE and the record changed; a source tag is exactly that test, and "You entered" fails
     * it. A field they typed is not hidden — its row is right below, ticked and tagged. */
    const addedLabels = sourcedAdditions(
      added.filter((k) => !(addressSettled && k === "address")), grid).map(label);
    const stillMissing = missingRequiredFields(grid).map((f) => f.label);
    /* OPTIONAL ROWS THE RECORD DID NOT CARRY (2026-08-06, owner defect). Everything that is not
     * carried over, not required, and not something a source actually landed. On the owner's job
     * this was exactly `acres` and `half_baths`, and because they were the only rows left asking a
     * question they read as the last two things standing between him and a report. They are not
     * work. `ConfirmGrid` puts them in their own collapsed, honestly-labelled block, and refuses to
     * accept a required key here, so no readiness gate moves.
     *
     * `acres` is dropped from the list entirely once the lot size is confirmed — see the note on
     * `acresHidden` below.
     *
     * ONLY WHEN THERE IS A STEP 1, for the same reason the carry-over summary is: an agent who
     * skipped the lookup is seeing every one of these fields for the FIRST time here, and the whole
     * screen is legitimately a form. The defect is specific to a second screen that had two rows
     * left on it and made them look like the finish line. */
    const optionalKeys = !step1Grid ? [] : SUBJECT_FIELD_SCHEMA
      .filter((f) => !f.required)
      .map((f) => f.key)
      .filter((k) => !carried.includes(k) && !added.includes(k) && !isFieldConfirmed(grid, k));
    /* THE ACRES ROW HAS NO JOB ONCE THE SQUARE FOOTAGE IS CONFIRMED (2026-08-06, owner). It exists
     * for an MLS sheet that prints acres and no square feet; `_fill_lot_from_acres` converts that
     * before the grid is ever built. What it was actually doing on this screen was sitting under a
     * near-identical label one row below the confirmed lot size, inviting the square footage to be
     * typed into it a second time — which is what happened, and which then killed the build at
     * compute time. A row that can only introduce a conflict and can never add a fact is not shown.
     * It comes straight back the moment the lot size is unticked, because then it can help again. */
    const acresHidden = isFieldConfirmed(grid, "lot_sf");
    const hiddenKeys = [
      ...(addressSettled ? ADDRESS_ONLY : []),
      ...(acresHidden ? ACRES_ONLY : []),
    ];
    /* AND STEP 1 IS NOT ALWAYS "YOUR FILE" (2026-08-11). Since the source cascade an agent can
       reach this screen from an address and nothing else, and a Back button naming a file they
       never uploaded points at a screen they will not recognise. Step 1 is where the address is
       either way, so that is what it is called when there is no file to go back to. */
    const backLabel = fileNames.length ? "Back to your file" : "Back to the address";
    return (
      <AppShell step="detail" content="full" onBack={() => setStep("intake")} backLabel={backLabel}
              onStepSelect={handleStepSelect} canGoToStep={canGoToStep}
              whyStepIsClosed={whyStepIsClosed}>
        <div style={{ maxWidth: measure.prose }}>
          {/* THE PROTOTYPE'S HEAD (2026-08-30). Title and subject on one row at 15px/12.5px, in
              place of a 22px h1 over a 15px line over a 14px paragraph. The address keeps its
              source tag: it is a figure like any other and states where it came from. */}
          <div className="vb-head">
            <h1>This home</h1>
            {addressSettled && (
              <span className="c">
                <strong style={{ color: colors.textPrimary }}>{subjectAnchor}</strong>
                {" "}· {grid.address?.sourceTag ?? "You entered"}
              </span>
            )}
          </div>
          {/* THE INSTRUCTION IS NOT REPEATED (2026-08-05). "Correct anything that's wrong" is the
              right thing to say the first time these fields are put in front of someone, and the
              wrong thing to say to someone who did exactly that one screen ago — it reads as the
              app not having kept what they did. */}
          <p style={{ color: colors.textSecondary, margin: "0 0 14px", fontSize: 12.5, lineHeight: 1.55, maxWidth: "78ch" }}>
            {step1Grid
              ? "What you confirmed is kept. This screen is what your file added on top of it, anything still missing, and the two things the comparables can't know."
              : "Every field is source-tagged, and nothing reaches the report unless its checkbox is checked. Check, uncheck, or correct anything that's wrong — a corrected figure keeps the original on record."}
          </p>
          {banners}
          {/* THE THREE THINGS THE CASCADE HAS TO SAY, IN THE ORDER THEY MATTER (2026-08-11).
              First whether the figures below are even real (`env: sandbox` is live in production),
              then whether a source went unasked — which is NOT the same as a source with nothing to
              say, and is the one distinction in this payload that must never be treated as
              cosmetic. Both are rendered before the grid, because both change how everything under
              them should be read. The gap list is a question and waits until after it. */}
          <CascadeNotices notices={cascade?.notices} />
          <CouldNotAskPanel meta={cascade} onRetry={retryCascade} retrying={resolving}
                            onAddFile={() => addFileInputRef.current?.click()} />
          {/* WHERE THE CONTROL ACTED. Mounted beside the outage panel, on the same screen the
              agent pressed for data — not at the top of the page and not in a toast. Renders
              nothing unless the server set `cad_required`, which it does ONLY when MLS, Cotality
              and the county record have all failed for this address and no file was uploaded. */}
          <CadRequiredPanel
            meta={cascade}
            onProvide={() => addFileInputRef.current?.click()}
            provideLabel="Upload a CAD sheet"
          />
          {skippedUpload && (
            <UploadSkippedPanel
              fileNames={skippedUpload.files.map((f) => f.name)}
              busy={phase !== "idle"}
              onUseFile={() => {
                // BACK TO WHERE READING A FILE IS SHOWN (measured in a real browser, 2026-09-22).
                // The four-stage progress panel and the extract's own error both render on the
                // intake step. Pressed here without moving, the agent saw a disabled button for
                // half a minute and a failed read reported on a screen they were not looking at.
                setStep("intake");
                void readFiles(skippedUpload);
              }}
            />
          )}
          {/* HOW THE SUBJECT WAS RECONCILED, ON ITS OWN AND NOT BEHIND ANYTHING (2026-08-05, review
              finding). This used to be a prop of `SubjectCarryOver`, which renders only when there
              is a step 1 grid to diff against — so on a restored draft, or for an agent who skipped
              the lookup, the app decided something about which property this report is for and told
              nobody. The carry-over summary is a nicety and may come and go with `step1Grid`; this
              is a disclosure and renders whenever there is one. */}
          {subjectNotice && (
            <p
              role="status"
              style={{
                margin: "14px 0 0", padding: "12px 14px", borderRadius: 10,
                background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`,
                fontSize: 13, lineHeight: 1.55, color: colors.textPrimary,
              }}
            >
              {subjectNotice}
            </p>
          )}
          {/* ONLY WHEN THERE IS A STEP 1 TO DIFF AGAINST. An agent who skipped the lookup is seeing
              these fields for the FIRST time here, and "nothing has changed since" would be a
              summary of a screen they never used. */}
          {step1Grid && (
            <SubjectCarryOver
              carriedCount={quietKeys.length + (addressSettled ? 1 : 0)}
              addedLabels={addedLabels}
              missingLabels={stillMissing}
              backLabel={backLabel}
            />
          )}
        </div>

        <div style={{ marginTop: 20 }}>
          <ConfirmGrid
            briefKey={jobId}
            grid={grid}
            onChange={setGrid}
            condition={condition}
            onConditionChange={setCondition}
            comps={comps}
            onCompsChange={setComps}
            addressLine={subjectAnchor}
            sections="subject"
            minComps={minComps}
            cadPick={subjectCadPick}
            /* Address: asked once, on step 1, in a box of its own — and printed above.
               Acres: withdrawn once the square footage is confirmed — see `acresHidden`. */
            hideKeys={hiddenKeys.length ? hiddenKeys : undefined}
            /* Already confirmed one screen ago: shown, not asked. */
            quietKeys={quietKeys}
            /* Optional, and nothing on record for them: shown as optional, not as work. */
            optionalKeys={optionalKeys}
            quietTitle={`Carried over from the last screen · ${quietKeys.length}`}
            quietBlurb="You confirmed these when you looked the address up. Nothing here has changed since, and nothing needs doing to them. Open them if you want to correct one."
            /* The ladder was set on step 1 and the same dropdown two screens running reads as the
               app having forgotten. The standing answer is stated; the control is one click away,
               with the dollar-scale note that only makes sense once the comps are read. */
            conditionMode={step1Grid ? "confirm" : "ask"}
            /* Keyed on the address alone, so step 1's copy and this one were the same image. */
            hideReferencePhoto={!!step1Grid}
            rungLabels={cascade?.rung_labels}
            blankNote={subjectBlankNote}
          />
        </div>

        {/* WHAT NO RECORD STATES ABOUT THIS HOME — questions, in neutral type, never an error list
            (spec §4). The fields themselves are the labelled blanks in the grid above; this says
            WHY each one is blank, which is the part a form cannot say for itself. */}
        {extractJobId && (
          <SubjectPhotoPicker
            where="review"
            options={photoOptions}
            chosenKey={photoChoiceKey}
            uploadedInstead={!!confirmedSubjectPhoto && !photoChoiceKey}
            onChoose={choosePhotoOption}
            onClear={() => { setConfirmedSubjectPhoto(null); setPhotoChoiceKey(null); }}
            onRetry={retryPhotoOptions}
          />
        )}
        <SubjectPhotoUpload
          where="review"
          photoOptionsShown={photoOptions?.kind === "ok" && photoOptions.photos.length > 0}
          photo={confirmedSubjectPhoto ?? extractPhoto?.photo ?? null}
          origin={confirmedSubjectPhoto
            ? (photoChoiceKey ? "the photo you chose above" : "the photo you uploaded here")
            : extractPhoto?.source === "subject_upload"
              ? "the photo you added at the start"
              : "the photo read from your subject sheet"}
          onPhoto={(uri) => { setConfirmedSubjectPhoto(uri); setPhotoChoiceKey(null); }}
        />
        <GapQuestions gaps={cascade?.gaps} scope="subject" />

        <section style={{ marginTop: 30, paddingTop: 24, borderTop: `1px solid ${colors.border}` }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>What the comparables don&apos;t know</h2>
          <p style={{ color: colors.textSecondary, marginTop: 6, marginBottom: 18, fontSize: 14, lineHeight: 1.5, ...blockStyle("copy") }}>
            Two boxes, on purpose. One is for things you can put a number on, and that number is
            applied to the estimate with your name against it. The other is for everything else, and
            it changes how the brief reads without moving a figure.
          </p>
          <UpgradesPanel
            value={valueAdjustments}
            onChange={setValueAdjustments}
            extraContext={extraContext}
            onExtraContextChange={setExtraContext}
            subjectHasPool={subjectHasPool}
            marketConditions={marketConditions}
            onMarketConditionsChange={setMarketConditions}
          />
        </section>

        {addFileInput}

        <SubjectStepGate
          grid={grid}
          onFetchPublicRecords={handleFetchPublicRecords}
          lockedRecordNote={lockedRecordNote}
          onAddFile={() => addFileInputRef.current?.click()}
          /* Through `requestForward`, not straight to step 3 — the address row is editable on this
             screen whenever it is not already confirmed, so this is the second door a moved subject
             can reach the comparable set through. */
          onContinue={() => requestForward("comps")}
          fetchingPublicRecords={fetchingPublicRecords}
          merging={merging}
          limitRefusalMessage={limitRefusal}
        />
      </AppShell>
    );
  }

  // ---- STEP 3 — the comparable set, the competition, and the map ---------------------------
  if (step === "comps") {
    const recommended = comps.filter((c) => c.selected === true).length;
    return (
      <AppShell step="comps" content="data" onBack={() => setStep("detail")} backLabel="Back to this home"
              onStepSelect={handleStepSelect} canGoToStep={canGoToStep}
              whyStepIsClosed={whyStepIsClosed}>
        {/* UI-060: the comps step is wide (the table needs it); the intro keeps a reading measure. */}
        <div style={{ maxWidth: measure.prose }}>
          <div className="vb-head">
            <h1>The comparable sales</h1>
            <span className="c">
              Closed sales price the report. Homes still on the market are the competition and are
              shown separately — an asking price is not a sale, and never enters the math.
            </span>
          </div>
          {/* WHERE THE TOOL THINKS THEY RANK. The server already sends its own recommendation
              (`selected`, the top set by its review ranking) and the client already starts from it;
              until now nothing on screen said so, which made a pre-ticked box look arbitrary. This
              states what was recommended and how many rows there were to choose from. It does not
              invent a per-comp reason — the ranking is by closeness on size, age and date, and
              claiming anything more specific than the server actually sends would be a guess. */}
          {/* ONE LINE, NOT TWO (2026-08-11). This sentence and the cascade's `FoundLine` below say
              the same thing in two registers — "we read 8 closed sales and haven't put any forward
              yet" under "we found 8 nearby sales from public records, check the ones you want to
              use" is the product telling the agent the same fact twice before they have done
              anything. The cascade's line is the one the spec asks for, so when it is going to
              render this one stands down. It stays for the upload path, where it says the thing the
              cascade line cannot: how many of them the server RECOMMENDED. */}
          {comps.length > 0 && !foundLine(cascade, comps.length) && (
            <p style={{ marginTop: 10, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
              {recommended > 0
                ? `We started with the ${recommended} closest of the ${comps.length} closed ${comps.length === 1 ? "sale" : "sales"} we read — nearest on size, age and sale date. Change any of it.`
                : `We read ${comps.length} closed ${comps.length === 1 ? "sale" : "sales"} and haven't put any forward yet. Pick the ones that fit.`}
            </p>
          )}
          {/* WHAT WE FOUND AND WHERE IT CAME FROM, IN ONE PLAIN LINE (spec §4) — "We found 8 nearby
              sales from public records. Check the ones you want to use." Not a provenance table:
              the per-row source is a column in the grid and the per-field tags sit in the cells,
              and neither of those is a thing to read before starting work.
              It renders NOTHING while a source could not be asked. That is structural, not a
              convention — `foundLine` and `noSalesLine` both refuse in that state, so there is no
              code path here that can print an empty-result sentence over an outage. */}
          {/* NO SALES CAME BACK (owner, 2026-10-19). A regular user gets the calm card: what to do
              next, no provider named. The diagnostics allow-list keeps the detailed lines and the
              "Source diagnostics" panel — the server decides who is on it (`provider_diag`). */}
          {showNoCompsCard ? (
            <NoCompsCard mlsConnected={cascadeStatus?.gates?.mls?.available === true}
                         mlsOffered={mlsOffered}
                         onUpload={() => addFileInputRef.current?.click()}
                         onAdjustSearch={() => { setCompSearchOpen(true); setCompSearchFocusTick((n) => n + 1); }} />
          ) : (
            <>
              <FoundLine meta={cascade} compCount={comps.length} />
              <CompMlsRow available={cascadeStatus?.gates?.mls?.available} />
            </>
          )}
          <SourceDiagnosticsPanel rows={cascade?.provider_diagnostics} />
          <CascadeNotices notices={cascade?.notices} />
          <CouldNotAskPanel meta={cascade} onRetry={retryCascade} retrying={resolving}
                            onAddFile={() => addFileInputRef.current?.click()} />
          {/* WHERE THE CONTROL ACTED. Mounted beside the outage panel, on the same screen the
              agent pressed for data — not at the top of the page and not in a toast. Renders
              nothing unless the server set `cad_required`, which it does ONLY when MLS, Cotality
              and the county record have all failed for this address and no file was uploaded. */}
          <CadRequiredPanel
            meta={cascade}
            onProvide={() => addFileInputRef.current?.click()}
            provideLabel="Upload a CAD sheet"
          />
          {skippedUpload && (
            <UploadSkippedPanel
              fileNames={skippedUpload.files.map((f) => f.name)}
              busy={phase !== "idle"}
              onUseFile={() => {
                // BACK TO WHERE READING A FILE IS SHOWN (measured in a real browser, 2026-09-22).
                // The four-stage progress panel and the extract's own error both render on the
                // intake step. Pressed here without moving, the agent saw a disabled button for
                // half a minute and a failed read reported on a screen they were not looking at.
                setStep("intake");
                void readFiles(skippedUpload);
              }}
            />
          )}
          {banners}
        </div>

        <CompSearch
          open={compSearchOpen}
          onOpenChange={setCompSearchOpen}
          focusRequest={compSearchFocusTick}
          subject={{
            address: subjectAnchor,
            query: subjectPinQuery,
            gla: numFromGrid(grid.gla?.value),
            glaSource: glaSourceWords(grid.gla?.sourceTag, !!grid.gla?.edited),
            lot: numFromGrid(grid.lot_sf?.value),
            lotSource: grid.lot_sf?.edited ? "You entered" : (grid.lot_sf?.sourceTag ?? null),
            year: numFromGrid(grid.year?.value),
            beds: numFromGrid(grid.beds?.value),
            subdivision: String(grid.subdivision?.value ?? "").trim() || null,
          }}
          comps={comps}
          onCompsChange={setComps}
          onListingsChange={setListings}
          onMeta={setCascade}
          cascadeSummary={cascade?.comp_search ?? null}
          heldBack={heldBack}
          actives={listings}
          activesLine={activesSearchLine(cascade !== null, cascade?.mls, extractJobId ? listings.length : 0)}
          buildArgs={() => cascadeArgsWithPick(
            { ...(lastResolveArgs.current ?? { address: subjectAnchor }), agent_subject: agentSubjectFromGrid(grid) },
            cadPickRef.current?.lock)}
          rungLabels={cascade?.rung_labels}
          order={compShownOrder}
          activeIndex={compActive}
          onActiveChange={setCompActive}
          onPinClick={(i: number) => {
            const card = document.querySelector<HTMLElement>(`[data-comp-index="${i}"]`);
            card?.scrollIntoView?.({ block: "nearest" });
            card?.querySelector<HTMLElement>("input")?.focus();
          }}
        />

        <div style={{ marginTop: 20 }}>
          <ConfirmGrid
            briefKey={jobId}
            grid={grid}
            onChange={setGrid}
            condition={condition}
            onConditionChange={setCondition}
            comps={comps}
            onCompsChange={setComps}
            compFocus={{ activeIndex: compActive, onActiveChange: setCompActive, onOrder: setCompShownOrder }}
            bestOrder={bestOrder}
            bestOrderNote={bestNote}
            sections="comps"
            /* THE COMPARABLE SET IS A JUDGEMENT ABOUT HOUSES BEFORE IT IS A TABLE OF FIGURES
               (2026-08-29). Cards lead here; the table is one click away and is still where a
               number gets corrected. `ConfirmGrid`'s own default stays "table" — see the note on
               `compView` there for why the component and the screen differ on purpose. */
            defaultCompView="cards"
            minComps={minComps}
            rungLabels={cascade?.rung_labels}
          />
        </div>

        {/* WHAT THE SALES RECORDS DON'T SAY — the garage count public records carry no field for,
            the full/half split the feed collapses into one number, a sale price the record omitted.
            Questions with the server's own reason attached, in neutral type. The cells they name
            are the empty inputs in the table above. */}
        <GapQuestions gaps={cascade?.gaps} scope="comp" title="What the sales records don't say" />

        <PriorSaleNotice priorSales={priorSales} />
        {/* THE COMPETING-HOMES SEARCH, INDEPENDENT OF THE SOLD ONE (actives-search slice). */}
        {(listings.length > 0 || cascadeStatus?.gates?.mls?.available === true) && !cascade?.comp_search?.acreage && (
        <ActivesSearch
          listings={listings}
          onListingsChange={setListings}
          subjectGla={numFromGrid(grid.gla?.value)}
          subject={{
            gla: numFromGrid(grid.gla?.value), lot_sf: numFromGrid(grid.lot_sf?.value),
            year: numFromGrid(grid.year?.value), subdivision: String(grid.subdivision?.value ?? "").trim() || null,
            condition_code: String(grid.condition_code?.value ?? "").trim() || null,
          }}
          appliedRadius={cascade?.mls?.actives_searched === true ? (cascade.mls.actives_radius_mi ?? null) : null}
          mlsAvailable={cascadeStatus?.gates?.mls?.available === true}
          mlsReason={cascadeStatus?.gates?.mls?.available === true ? null : (cascade?.mls?.actives_reason ?? null)}
          buildArgs={() => cascadeArgsWithPick(
            { ...(lastResolveArgs.current ?? { address: subjectAnchor }), agent_subject: agentSubjectFromGrid(grid) },
            cadPickRef.current?.lock)}
          onView={setListingsView}
        />
        )}
        <CompetingListings listings={listings} onListingsChange={setListings} contextOnly={!!cascade?.comp_search?.acreage}
                           hidden={listingsView.hidden} order={listingsView.order} reasons={listingsView.reasons} />
        {/* THE MAP LIVES IN THE SEARCH PANEL ABOVE (1G): it shows every candidate, numbered like the
            cards. The review step keeps its own map of the sales that were chosen. */}

        {/* THE CLIENT'S NOTES FIRST, THEN THE SERVER'S. Both are "what you were told while your
            files were read"; they are two arrays only because `applyExtract` replaces the server's
            wholesale and must not be able to take the client's with it. */}
        <ReadingNotes gaps={[...reconcileNotes, ...gaps]} />
        <FileReadReceipt files={fileStatus} />

        {addFileInput}

        <CompsStepGate
          comps={comps}
          onAddFile={() => addFileInputRef.current?.click()}
          onContinue={() => { setComps(confirmSelected); requestForward("review"); }}
          merging={merging}
          minComps={minComps}
          limitRefusalMessage={limitRefusal}
        />
      </AppShell>
    );
  }

  // ---- STEP 4 — review everything, choose the tier and voice, generate ---------------------
  if (step === "review") {
    /* The same two conditions `ReadinessGate` applies, read here so the heading agrees with it. */
    const reviewReady = isGridReady(grid) && hasEnoughComps(comps, minComps);
    return (
      <AppShell step="review" content="form" onBack={() => setStep("comps")} backLabel="Back to the comparable sales"
              onStepSelect={handleStepSelect} canGoToStep={canGoToStep}
              whyStepIsClosed={whyStepIsClosed}>
        {/* THE MEASURE STOPS AT THE PROSE (2026-08-11). This wrapper used to enclose the whole
            screen — the heading, the summary cards, the map and the name box — while the readiness
            gate below it sat outside at the page measure. The result was a ragged right edge: cards
            ending at 880 with a full-width green panel underneath them. The reading measure belongs
            to the sentences; everything that is a panel takes the page's width. */}
        <div style={{ maxWidth: measure.prose }}>
          {/* THE HEADING TELLS THE TRUTH ABOUT THE STATE IT IS IN (2026-09-07).
              It read "Ready to generate" unconditionally, and since 9d6b320 opened the rail this
              screen is reachable from step 1 — where it announced readiness directly above an amber
              panel saying the report cannot compute. Photographed at 1512: "Ready to generate" over
              "We couldn't confirm Address and Living area (GLA) and Lot size…". The screen is
              genuinely useful in both states — it is the only place that lists exactly what is
              still needed — so the fix is the title, not the door.
              THE READINESS TEST IS `ReadinessGate`'s OWN, imported rather than restated, so the
              heading and the gate below it cannot come to different conclusions. */}
          <div className="vb-head">
            <h1>{reviewReady ? "Ready to generate" : "Not ready yet"}</h1>
            <span className="c">
              {reviewReady
                ? "This is everything the report will be built from. Anything here can still be changed — each line says which step owns it."
                : "This is everything the report will be built from, and what is still needed is at the foot of this screen. Anything here can be changed — each line says which step owns it."}
            </span>
          </div>
          {banners}
        </div>

        <ReviewSummary
          subjectAnchor={subjectAnchor}
          grid={grid}
          condition={condition}
          comps={comps}
          listings={listings}
          valueAdjustments={valueAdjustments}
          marketConditions={marketConditions}
          extraContext={extraContext}
          subjectHasPool={subjectHasPool}
          onEditSubject={() => setStep("detail")}
          onEditComps={() => setStep("comps")}
        />

        {/* THE MAP BELONGS ON REVIEW (owner, 2026-08-10: "Lets show the map with pin drops on the
            review so the agents"). It was mounted only inside the comps step, even though
            `LocationMap`'s own docstring says "the location map on the review screen" — the code
            and its documentation had drifted apart. Review is the last look before a figure
            becomes a document, and geographic spread is the check an agent makes there: three
            comps clustered on one street support a number in a way three scattered across a
            submarket do not. */}
        <LocationMap markers={mapPins} unmapped={mapResult.droppedAddresses} />

        {/* The one thing on this screen that is an INPUT rather than a summary, and it sits here
            rather than in `ReviewSummary` because that component reads state and never writes it.
            It belongs on step 4 for the same reason the voice picker does: both are choices about
            how the finished brief is presented, made at the moment the agent is deciding to
            generate it, not while they were confirming square footage two screens ago. */}
        <HomeownerName
          value={preparedFor}
          onChange={setPreparedFor}
          ownerOfRecord={cadIdentity?.owner_name && cadSource
            ? { name: cadIdentity.owner_name, source: cadSource }
            : null}
          useOwnerOfRecord={useOwnerOfRecord}
          onUseOwnerOfRecord={setUseOwnerOfRecord}
        />

        {addFileInput}

        <ReadinessGate
          grid={grid}
          comps={comps}
          onFetchPublicRecords={handleFetchPublicRecords}
          lockedRecordNote={lockedRecordNote}
          onAddFile={() => addFileInputRef.current?.click()}
          onSubmit={handleSubmit}
          submitting={submitting}
          merging={merging}
          fetchingPublicRecords={fetchingPublicRecords}
          loadedCounties={loadedCounties}
          selectedCounty={selectedCounty}
          onSelectedCountyChange={setSelectedCounty}
          tier={tier}
          onTierChange={setTier}
          personas={personas}
          onPersonasChange={setPersonas}
          minComps={minComps}
          onFixSubject={() => setStep("detail")}
          onFixComps={() => setStep("comps")}
          limitRefusalMessage={limitRefusal}
        />
      </AppShell>
    );
  }

  // step === "build"
  // A MULTI-VOICE RUN NEVER SHOWS A REPORT ON SCREEN (2026-08-04, owner directive). An inline
  // report can only render ONE narrative, so putting one of five on the page would silently
  // promote it to "the" report — the agent reads that one, downloads all five, and cannot tell
  // which they reviewed. The comparison is the deliverable here, so the screen is the download set.
  //
  // A RE-VOICE LANDS HERE TOO (2026-08-07). Asking for a second voice from the finished report
  // takes the count above one, so the screen becomes the download set for exactly the reason above:
  // leaving voice 1's narrative on the page while voice 2 writes would make voice 1 look like the
  // report. Both voices' files are listed, which is the point — the agent keeps the first.
  if (personaJobs.length > 1) {
    return (
      <AppShell step="build" content="form" onStepSelect={handleStepSelect} canGoToStep={canGoToStep}
              whyStepIsClosed={whyStepIsClosed}>
        <PersonaDownloads
          jobs={personaJobs}
          onStartOver={handleStartOver}
          /* The same disclosure the single-report screen makes, on the screen that has no re-voice
             control to withdraw. Nothing here can start a build, so there is nothing to block — but
             an agent who went back, changed a figure and returned to a list of download links has
             to be told those files predate the change. */
          editedSinceBuild={editedSinceBuild}
          buildUnverifiable={payloadWithheld && !confirmedPayload}
          /* NAVIGATION rule 6 on this screen too. Before this it offered one exit — start over with
             a different file — which is the most expensive thing an agent on this screen can do and
             was the only thing on offer. Same test as the report screen's own back. */
          onBackToReview={hasReview ? () => setStep("review") : undefined}
        />
      </AppShell>
    );
  }
  // UI-102: never render a blank page. NarrativeReportScreen handles a null job + an error string.
  //
  // THE REPORT STEP MOVED OFF THE PROSE MEASURE (2026-08-30). Measured on the owner's own screen:
  // 879px of content in a 3794px viewport — 23% of the glass, the rest empty ground either side.
  // `measures.ts` is right that uncapping a single column of prose only moves the margin to one
  // side rather than filling anything, so this is NOT `full`: it is `form` (1200), symmetric and
  // 36% wider, which the format picker now genuinely fills as a `.typeGrid` of four across instead
  // of a stack of four. `full` waits on the prototype's own answer for this screen — a full-width
  // `.cards` metric band over a `.paper` — which is a layout change per screen, not a constant.
  return (
    <AppShell step="build" content="form" onStepSelect={handleStepSelect} canGoToStep={canGoToStep}
              whyStepIsClosed={whyStepIsClosed}>
      <NarrativeReportScreen
        job={job}
        notice={notice}
        error={submitError}
        stalled={stalled}
        view={reportView}
        onViewChange={setReportView}
        onBackToReview={Object.keys(grid).length ? () => setStep("review") : undefined}
        onStartOver={handleStartOver}
        /* ANOTHER VOICE, FROM THE FINISHED REPORT (2026-08-07). Offered only when the payload that
           built this report is actually in hand — see `handleRevoice`. Absent, the control is not
           rendered at all rather than rendered and refusing: there is nothing the agent could do to
           make it work from that screen, so a disabled button would only be a puzzle. */
        onRevoice={confirmedPayload ? handleRevoice : undefined}
        revoiceWithheld={payloadWithheld && !confirmedPayload}
        /* A photo set on the finished report is kept here too, so every later voice carries it. */
        onSubjectPhoto={(uri) => { setConfirmedSubjectPhoto(uri); setPhotoChoiceKey(null); }}
        photoOptions={extractJobId ? photoOptions : undefined}
        photoChoiceKey={photoChoiceKey}
        onPhotoChoice={setPhotoChoiceKey}
        onPhotoRetry={retryPhotoOptions}
        /* What they already have, so a voice is never offered as new when a file for it exists.
           `personaJobs` is the truth once a submit has happened; `personas` covers a draft written
           before it was persisted. */
        /* Voices opened into their own tab count as built. They are not in `personaJobs` -- that
           list drives the download screen and `length > 1` swaps this one away, which is the exact
           behaviour the owner asked to stop. */
        builtPersonas={[
          ...(personaJobs.length ? personaJobs.map((p) => p.persona) : personas),
          ...revoicedAway.map((p) => p.persona),
        ]}
        revoicing={revoicing}
        revoiceError={revoiceError}
        revoiceBlocked={revoiceBlocked}
        /* NO VOICE IS OFFERED OVER AN EDIT THE REPORT DOESN'T CONTAIN (2026-08-07). A re-voice
           re-sends the stored payload unchanged — which is the guarantee two voices of one analysis
           rest on, and quietly wrong the moment the agent has corrected something since. The screen
           swaps the voice buttons for the one move that is right: go back and generate again. */
        editedSinceBuild={editedSinceBuild}
        onRebuildWithEdits={Object.keys(grid).length ? () => setStep("review") : undefined}
      />
    </AppShell>
  );
}
